import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Truck,
  Package,
  Search,
  RefreshCw,
  AlertCircle,
  FileText,
  Save,
  BookOpen,
  CheckCircle2,
  Circle,
  ArrowRight,
  MapPin,
  MessageSquare,
  Lock,
  Clock,
  Building,
  Upload,
  Warehouse,
  Settings,
  ExternalLink,
  Image,
  Edit3,
  Calendar,
  Calculator,
  Database,
  ChevronDown,
  ChevronRight,
  Square,
  CheckSquare,
  Download,
  X,
  DollarSign,
  Train
} from 'lucide-react';
import * as XLSX from 'xlsx';

// Leaflet map imports
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Routing app imports (tenant-aware)
import { ROUTING_SCHEMAS, getAllRoutingSchemas } from '../config/routingSchemas';
import { RoutingDataService } from '../services/RoutingDataService';
import { RoutingNotesService } from '../services/RoutingNotesService';
import { ShipPointsService } from '../services/ShipPointsService';
import { TenantUsersService } from '../services/TenantUsersService';
import { DocumentsService } from '../services/DocumentsService';
import { FirebaseService } from '../../../shared/services/FirebaseService';

// Note modal component
import NoteModal, { getReasonLabel, getResponsibilityLabel } from './NoteModal';

// Icons for user management
import { Users, UserPlus, Trash2, ToggleLeft, ToggleRight, Mail, Key, LogOut } from 'lucide-react';

// Firebase functions
import { getFunctions, httpsCallable } from 'firebase/functions';

// Tenant-aware file upload component
import RoutingFileUpload from './RoutingFileUpload';

// Transit Time Map component
import TransitTimeMap, { MODE_COLORS, TYPE_COLORS, WALMART_DCS, LOCATION_COORDINATES } from './TransitTimeMap';

// ============================================================================
// PLAN DOCUMENT CONTENT
// ============================================================================
const PLAN_CONTENT = {
  overview: `Build a reusable Walmart/Sam's routing system designed as a SaaS product that can scale to serve multiple suppliers (tenants). Customers provide whatever data they want - we take it and use it. More data sources = more products we can sell (Target TMS, other retailers). Platform-level features (DC data, transit times, status logic) shared across tenants; tenant-level data (ERP, Warehouse, TMS) isolated per customer.`,

  dataApproach: `If customers want to provide data, we take it and integrate it. The more data they share (ERP, Warehouse, TMS), the more value we can provide. This opens doors to sell additional products like Target TMS routing.`,

  keyInsight: `When an order becomes Prepaid, Walmart removes it from their TMS. To track prepaid orders, we need ERP data (Part 1 Orders).`,

  statusFlow: [
    { num: 1, status: 'Not Routed', desc: 'PO on pick (ERP) but not in WM tables' },
    { num: 2, status: 'Routed', desc: 'In WM tables but no load yet' },
    { num: 3, status: 'Confirmed', desc: 'Has load ID assigned' },
    { num: 4, status: 'Scheduled', desc: 'Has carrier pickup date' },
    { num: 5, status: 'Shipped', desc: 'Invoiced in ERP or warehouse log shows shipped' },
  ],

  shipmentTypes: [
    { type: 'Collect', desc: 'Walmart arranges carrier (standard)' },
    { type: 'Prepaid', desc: 'We ship prepaid - track carrier name & cost' },
  ],

  uiDecisions: [
    'Show all columns always - even if data isn\'t available (empty is fine). No dynamic hiding based on tier.',
    'Notes field with ticket tracking: free text area, checkbox for "Ticket Sent", ticket number field.',
    'Prepaid tracking: carrier name, carrier cost (for later analysis), notes explain why.',
  ],

  tickets: `Only opened in Walmart system when there's a problem (not routine). Critical to track for later chargebacks. Walmart audits and charges back for wasted space (overestimated pallets/volume), OTIF violations. Having ticket reference = defense against chargebacks.`,

  plannedComponents: [
    { name: 'Summary Cards', desc: 'Quick metrics: Scheduled, Not Routed, Prepaid totals' },
    { name: 'Appointment Matrix', desc: 'Appointment vs MABD breakdown - click to filter' },
    { name: 'Ship To Map', desc: 'USA map showing shipping destinations by DC location' },
    { name: 'PO Information Table', desc: 'Full details with Notes column' },
    { name: 'Routing Notes System', desc: 'Search by PO/Pick/Load, add notes with reason/responsibility (separate tab, user-approved access)' },
  ],

  dashboardLayout: [
    { pos: 'A1', name: 'Filters & Summary Cards', desc: 'Warehouse filter, search box, 4 summary cards (Scheduled, Not Routed, Prepaid, Total)' },
    { pos: 'B1', name: 'Appt vs MABD Matrix', desc: 'Cross-tab of MABD dates vs Appointment dates, shows $ or Cases' },
    { pos: 'B2', name: 'Pending by Pick Window', desc: 'Orders grouped by pick window (Today, Tomorrow, This Week, Overdue)' },
    { pos: 'C1', name: 'PO Information Table', desc: 'Main order table with LoadID, PO, PT, WH, MABD, Amount, Stage, Appt, Cases' },
    { pos: 'C2', name: 'Split Shipments', desc: 'POs with multiple picks, shows pick count and total amount' },
    { pos: 'D1', name: 'Open Confirmed by MABD', desc: 'ERP DATA REQUIRED - Orders confirmed but not shipped, grouped by MABD' },
    { pos: 'D2', name: 'Volume by Day', desc: 'ERP DATA REQUIRED - Order volume by day of week' },
    { pos: 'D3', name: 'Style Details', desc: 'ERP DATA REQUIRED - SKU/style level breakdown' },
    { pos: 'E1', name: 'Ship To Map', desc: 'USA map with markers showing shipment destinations (2/3 width)' },
    { pos: 'E2', name: 'TBD', desc: 'Placeholder for future widget (1/3 width)' },
  ],

  tabs: [
    { name: 'Dashboard', desc: 'Main routing dashboard with all visuals and tables' },
    { name: 'Data Workflow', desc: 'Data source management - connect ERP, Warehouse, TMS data. Includes Store Data and Transit Time uploads under Walmart Routing Data.' },
    { name: 'Notes', desc: 'Routing notes entry - user-approved access only. Notes display in dashboard.' },
    { name: 'Transit Time', desc: 'View transit time data showing shipping duration from origin to destination by mode (TL, Rail, Fleet).' },
    { name: 'Settings', desc: 'Configure routing system - Warehouse Setup (ship points), User Permissions, Notifications.' },
    { name: 'Feature Plan', desc: 'This documentation' },
  ],

  walmartTmsColumns: [
    { col: 'po_number', desc: 'Walmart/Sam\'s Purchase Order number' },
    { col: 'load_id', desc: 'Load identifier assigned when confirmed' },
    { col: 'load_dest', desc: 'Destination location' },
    { col: 'load_dest_type', desc: 'Store, DC, or CP (Central Point) - Sam\'s store = DC' },
    { col: 'load_dest_address', desc: 'Full address of destination' },
    { col: 'load_p_u_window_start', desc: 'Pickup window start' },
    { col: 'load_p_u_window_end', desc: 'Pickup window end' },
    { col: 'mabd', desc: 'Must Arrive By Date' },
    { col: 'carrier_pu_date', desc: 'Carrier pickup date (ship date)' },
    { col: 'carrier_due_date', desc: 'Carrier delivery due date' },
    { col: 'carrier_name', desc: 'Carrier name' },
    { col: 'industry_scac', desc: 'Standard Carrier Alpha Code' },
    { col: 'mode', desc: 'Rail, TL (Truck Load), Fleet (LTL to central point - takes longer)' },
    { col: 'ship_point', desc: 'Origin warehouse you\'re shipping from (needs setup)' },
    { col: 'loading_method', desc: 'Floor or Pallets - tells Walmart load time' },
    { col: 'cases', desc: 'Number of cartons' },
    { col: 'pallets', desc: 'Number of pallets' },
    { col: 'weight', desc: 'Total weight' },
    { col: 'cube', desc: 'Volume (cubic feet)' },
    { col: 'event_code', desc: 'Routing event code' },
    { col: 'po_type', desc: 'PO type' },
    { col: 'department', desc: 'Walmart or Sam\'s department number' },
    { col: 'confirmed_date', desc: 'When load was routed/confirmed' },
    { col: 'held_suspended', desc: 'Hold/suspend status' },
    { col: 'po_dest', desc: 'Where goods should go based on PO' },
  ],

  walmartTerms: [
    { term: 'MABD', name: 'Must Arrive By Date', desc: 'When goods must arrive at destination' },
    { term: 'DNSB', name: 'Don\'t Ship Before', desc: 'Tells Walmart goods will NOT be ready before this date' },
    { term: 'DNSA', name: 'Don\'t Ship After', desc: 'Tells Walmart we CAN\'T ship after this date' },
    { term: 'DNRB', name: 'Don\'t Route Before', desc: 'Goods won\'t be ready for routing until this date' },
  ],

  dataSources: [
    { step: 3, name: 'BL Routing', source: 'Google Sheet', table: 'bl' },
    { step: 4, name: 'LAP Routing', source: 'Google Sheet', table: 'lap' },
    { step: 5, name: 'Walmart Status', source: 'Google Sheets', table: 'walmart_mg_routing, walmart_bwi_routing, wm_tms_img, wm_tms_img_dsdc' },
  ],

  complexityPoints: [
    'Tickets - Track when opened, ticket number, for chargeback defense',
    'Audits - OTIF compliance tracking',
    'Split shipments - Multiple picks per PO',
    'Prepaid - Track carrier name and cost when order becomes prepaid',
    'Rollover/No show - Missed appointments',
    'Route when goods ready - Walmart prefers this (not "route and cancel")',
    'Aspen/GRS orders - Special handling',
  ],

  saasFeatures: [
    { name: 'Landing Page + Waitlist', desc: 'Marketing site, email capture, Firestore DB for waitlist', priority: 'High' },
    { name: 'Payment (Stripe)', desc: 'Subscription billing, plans, invoices', priority: 'High' },
    { name: 'User Management', desc: 'Invite users, roles, permissions per tenant', priority: 'High' },
    { name: 'Customer Service Ticketing', desc: 'Users report issues, track resolution', priority: 'Medium' },
    { name: 'Terms of Service', desc: 'Legal agreements, privacy policy', priority: 'High' },
    { name: 'ADA Compliance', desc: 'WCAG 2.1 AA - keyboard nav, screen readers, contrast', priority: 'Critical' },
  ],

  nextSteps: {
    phase1: [
      { task: 'Review mock UI', done: true },
      { task: 'Define data model and columns', done: true },
      { task: 'Implement data upload functionality', done: false, current: true },
      { task: 'Connect to BigQuery', done: false },
      { task: 'Build MABD Calculator logic', done: false },
    ],
    phase2: [
      { task: 'Multi-tenant data isolation', done: false },
      { task: 'User management & roles', done: false },
      { task: 'Tenant onboarding flow', done: false },
    ],
    phase3: [
      { task: 'Landing page + Waitlist (Firestore)', done: false },
      { task: 'ADA Compliance audit (WCAG 2.1 AA)', done: false },
      { task: 'Stripe payment integration', done: false },
      { task: 'Terms of Service / Privacy Policy', done: false },
      { task: 'Customer service ticketing', done: false },
    ],
  },
};

// ============================================================================
// Note: routing_status is calculated in the BigQuery Cloud Function
// Status values: Scheduled (has carrier_pu_date), Confirmed (has load_id), Pending
// ============================================================================

// Helper to safely extract value from BigQuery's {value: x} format
const safeValue = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object' && val !== null) {
    // Handle BigQuery's {value: x} format
    if ('value' in val) return safeValue(val.value); // Recursively extract
    // Handle nested objects
    return String(val); // Convert to string to prevent React errors
  }
  return val;
};

// Ship point colors for map bubbles
const SHIP_POINT_COLORS = {
  IMG: { bg: '#3b82f6', border: '#1d4ed8', name: 'IMG' },      // Blue
  LAP: { bg: '#8b5cf6', border: '#6d28d9', name: 'LAP' },      // Purple
  BL: { bg: '#f59e0b', border: '#d97706', name: 'BL' },        // Amber
  CLS: { bg: '#06b6d4', border: '#0891b2', name: 'CLS' },      // Cyan
  default: { bg: '#64748b', border: '#475569', name: 'Other' } // Slate (fallback)
};

// Format date as MM/DD (e.g., "01/20" for 2026-01-20) - matches BigQuery FORMAT_DATE('%m/%d')
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  // Handle ISO format (2026-01-20) or already formatted dates
  const str = String(dateStr);
  if (str.includes('-')) {
    const parts = str.split('-');
    if (parts.length === 3) {
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${month}/${day}`;
    }
  }
  return str; // Return as-is if not ISO format
};

// Get column style for matrix based on date (Confirmed=yellow, past=red, today=green)
const getMatrixColumnStyle = (col) => {
  if (col === 'Pending') {
    return { header: 'bg-amber-100 text-amber-800', cell: 'bg-amber-50', footer: 'bg-amber-100' };
  }
  if (col === 'Confirmed') {
    return { header: 'bg-blue-100 text-blue-800', cell: 'bg-blue-50', footer: 'bg-blue-100' };
  }
  // Parse MM/DD format and compare to today
  const today = new Date();
  const todayStr = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;
  const parts = col.split('/');
  if (parts.length === 2) {
    const colMonth = parseInt(parts[0], 10);
    const colDay = parseInt(parts[1], 10);
    const colDate = new Date(today.getFullYear(), colMonth - 1, colDay);
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (col === todayStr) {
      return { header: 'bg-emerald-100 text-emerald-800', cell: 'bg-emerald-50', footer: 'bg-emerald-100' };
    }
    if (colDate < todayDate) {
      return { header: 'bg-red-100 text-red-800', cell: 'bg-red-50', footer: 'bg-red-100' };
    }
  }
  return { header: '', cell: '', footer: '' };
};

// Reason options for notes (from LogisticsPage)
const REASON_OPTIONS = [
  { value: '', label: 'Select reason...' },
  { value: 'carrier_no_show', label: 'Carrier No Show' },
  { value: 'rollover', label: 'Rollover' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'warehouse_delay', label: 'Warehouse Delay' },
  { value: 'production_delay', label: 'Production Delay' },
  { value: 'split_shipment', label: 'Split Shipment' },
  { value: 'other', label: 'Other' },
];

const RESPONSIBILITY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'customer', label: 'Customer' },
  { value: 'inmocean', label: 'InMocean' },
  { value: 'vendor', label: 'Vendor' },
];

// ============================================================================
// USA MAP COMPONENT - Clean map with marker overlays
// ============================================================================
// State center coordinates for marker placement (x, y in 960x600 viewBox)
// State centers with lat/lng coordinates for Leaflet map
const STATE_COORDS = {
  AL: { name: 'Alabama', lat: 32.806671, lng: -86.791130 },
  AK: { name: 'Alaska', lat: 61.370716, lng: -152.404419 },
  AZ: { name: 'Arizona', lat: 33.729759, lng: -111.431221 },
  AR: { name: 'Arkansas', lat: 34.969704, lng: -92.373123 },
  CA: { name: 'California', lat: 36.116203, lng: -119.681564 },
  CO: { name: 'Colorado', lat: 39.059811, lng: -105.311104 },
  CT: { name: 'Connecticut', lat: 41.597782, lng: -72.755371 },
  DE: { name: 'Delaware', lat: 39.318523, lng: -75.507141 },
  FL: { name: 'Florida', lat: 27.766279, lng: -81.686783 },
  GA: { name: 'Georgia', lat: 33.040619, lng: -83.643074 },
  HI: { name: 'Hawaii', lat: 21.094318, lng: -157.498337 },
  ID: { name: 'Idaho', lat: 44.240459, lng: -114.478828 },
  IL: { name: 'Illinois', lat: 40.349457, lng: -88.986137 },
  IN: { name: 'Indiana', lat: 39.849426, lng: -86.258278 },
  IA: { name: 'Iowa', lat: 42.011539, lng: -93.210526 },
  KS: { name: 'Kansas', lat: 38.526600, lng: -96.726486 },
  KY: { name: 'Kentucky', lat: 37.668140, lng: -84.670067 },
  LA: { name: 'Louisiana', lat: 31.169546, lng: -91.867805 },
  ME: { name: 'Maine', lat: 44.693947, lng: -69.381927 },
  MD: { name: 'Maryland', lat: 39.063946, lng: -76.802101 },
  MA: { name: 'Massachusetts', lat: 42.230171, lng: -71.530106 },
  MI: { name: 'Michigan', lat: 43.326618, lng: -84.536095 },
  MN: { name: 'Minnesota', lat: 45.694454, lng: -93.900192 },
  MS: { name: 'Mississippi', lat: 32.741646, lng: -89.678696 },
  MO: { name: 'Missouri', lat: 38.456085, lng: -92.288368 },
  MT: { name: 'Montana', lat: 46.921925, lng: -110.454353 },
  NE: { name: 'Nebraska', lat: 41.125370, lng: -98.268082 },
  NV: { name: 'Nevada', lat: 38.313515, lng: -117.055374 },
  NH: { name: 'New Hampshire', lat: 43.452492, lng: -71.563896 },
  NJ: { name: 'New Jersey', lat: 40.298904, lng: -74.521011 },
  NM: { name: 'New Mexico', lat: 34.840515, lng: -106.248482 },
  NY: { name: 'New York', lat: 42.165726, lng: -74.948051 },
  NC: { name: 'North Carolina', lat: 35.630066, lng: -79.806419 },
  ND: { name: 'North Dakota', lat: 47.528912, lng: -99.784012 },
  OH: { name: 'Ohio', lat: 40.388783, lng: -82.764915 },
  OK: { name: 'Oklahoma', lat: 35.565342, lng: -96.928917 },
  OR: { name: 'Oregon', lat: 44.572021, lng: -122.070938 },
  PA: { name: 'Pennsylvania', lat: 40.590752, lng: -77.209755 },
  RI: { name: 'Rhode Island', lat: 41.680893, lng: -71.511780 },
  SC: { name: 'South Carolina', lat: 33.856892, lng: -80.945007 },
  SD: { name: 'South Dakota', lat: 44.299782, lng: -99.438828 },
  TN: { name: 'Tennessee', lat: 35.747845, lng: -86.692345 },
  TX: { name: 'Texas', lat: 31.054487, lng: -97.563461 },
  UT: { name: 'Utah', lat: 40.150032, lng: -111.862434 },
  VT: { name: 'Vermont', lat: 44.045876, lng: -72.710686 },
  VA: { name: 'Virginia', lat: 37.769337, lng: -78.169968 },
  WA: { name: 'Washington', lat: 47.400902, lng: -121.490494 },
  WV: { name: 'West Virginia', lat: 38.491226, lng: -80.954453 },
  WI: { name: 'Wisconsin', lat: 44.268543, lng: -89.616508 },
  WY: { name: 'Wyoming', lat: 42.755966, lng: -107.302490 },
};

// Map bounds updater component for auto-zoom
const MapBoundsUpdater = ({ shipmentsByState }) => {
  const map = useMap();

  useEffect(() => {
    const statesWithData = Object.keys(shipmentsByState);

    // If no states or too many states (>15), use default USA view
    if (statesWithData.length === 0 || statesWithData.length > 15) {
      map.setView([39.8283, -98.5795], 4);
      return;
    }

    // Get bounds from states with data
    const points = statesWithData
      .map(code => STATE_COORDS[code])
      .filter(Boolean)
      .map(coords => [coords.lat, coords.lng]);

    if (points.length === 0) {
      map.setView([39.8283, -98.5795], 4);
      return;
    }

    if (points.length === 1) {
      // Single state - zoom to it with padding
      map.setView(points[0], 6);
    } else {
      // Multiple states - fit bounds with padding
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 7 });
    }
  }, [shipmentsByState, map]);

  return null;
};

// Leaflet-based USA Map component
const USAMap = ({ data, onStateClick, selectedState }) => {
  // Aggregate shipments by state
  const shipmentsByState = useMemo(() => {
    const byState = {};
    data.forEach(order => {
      const state = safeValue(order.store_state) || safeValue(order.dc_state);
      const cases = parseInt(safeValue(order.cases)) || 0;
      const pallets = parseInt(safeValue(order.pallets)) || 0;
      const status = safeValue(order.routing_status);
      // Use warehouse field for ship point colors (IMG, LAP, BL, CLS)
      const warehouseRaw = safeValue(order.warehouse);
      const shipPoint = warehouseRaw ? String(warehouseRaw).toUpperCase().trim() : 'default';
      if (state && STATE_COORDS[state]) {
        if (!byState[state]) {
          byState[state] = {
            count: 0, cases: 0, pallets: 0,
            scheduled: 0, confirmed: 0, pending: 0,
            shipPoints: {} // Track cases by ship point
          };
        }
        byState[state].count++;
        byState[state].cases += cases;
        byState[state].pallets += pallets;
        if (status === 'Scheduled') byState[state].scheduled++;
        else if (status === 'Confirmed') byState[state].confirmed++;
        else byState[state].pending++;
        // Track cases by ship point
        if (!byState[state].shipPoints[shipPoint]) {
          byState[state].shipPoints[shipPoint] = 0;
        }
        byState[state].shipPoints[shipPoint] += cases;
      }
    });
    // Determine dominant ship point for each state
    Object.values(byState).forEach(stateData => {
      const shipPoints = stateData.shipPoints;
      let dominant = 'default';
      let maxCases = 0;
      Object.entries(shipPoints).forEach(([sp, cases]) => {
        if (cases > maxCases) {
          maxCases = cases;
          dominant = sp;
        }
      });
      stateData.dominantShipPoint = dominant;
    });
    return byState;
  }, [data]);

  const maxCases = Math.max(...Object.values(shipmentsByState).map(s => s.cases), 1);

  // Create custom icon with number inside - colored by ship point
  const createIcon = (code, info) => {
    const size = 24 + (info.cases / maxCases) * 20; // 24-44px
    const isSelected = selectedState === code;
    const isOther = selectedState && selectedState !== code;

    // Get color based on dominant ship point
    const shipPointColor = SHIP_POINT_COLORS[info.dominantShipPoint] || SHIP_POINT_COLORS.default;
    const bgColor = isSelected ? '#16a34a' : isOther ? '#cbd5e1' : shipPointColor.bg;
    const borderColor = isSelected ? '#15803d' : isOther ? '#94a3b8' : shipPointColor.border;
    const opacity = isOther ? 0.5 : 1;

    return L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: ${size}px;
        height: ${size}px;
        background: ${bgColor};
        border: 2px solid ${borderColor};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: ${size > 32 ? '12px' : '10px'};
        opacity: ${opacity};
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">${info.count}</div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  return (
    <MapContainer
      center={[39.8283, -98.5795]}
      zoom={4}
      style={{ height: '100%', width: '100%', borderRadius: '8px' }}
      scrollWheelZoom={true}
      dragging={true}
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
      />
      <ZoomControl position="topright" />
      <MapBoundsUpdater shipmentsByState={shipmentsByState} />
      {Object.entries(shipmentsByState).map(([code, info]) => {
        const coords = STATE_COORDS[code];
        if (!coords) return null;
        // Include ship point in key to force icon update when data changes (but not selectedState to keep popup open)
        const markerKey = `${code}-${info.dominantShipPoint}-${info.count}`;
        return (
          <Marker
            key={markerKey}
            position={[coords.lat, coords.lng]}
            icon={createIcon(code, info)}
          >
            <Popup>
              <div className="text-xs min-w-[160px]">
                <div className="font-bold text-slate-800 text-sm">{coords.name}</div>
                <div className="text-slate-600 mt-1">{info.count} orders</div>
                <div className="text-blue-600 font-semibold text-sm">{info.cases.toLocaleString()} cases</div>
                <div className="flex gap-2 mt-2 text-[10px]">
                  <span className="text-green-600">● {info.scheduled} sched</span>
                  <span className="text-blue-600">● {info.confirmed} conf</span>
                  <span className="text-yellow-600">● {info.pending} pend</span>
                </div>
                {/* Ship Point breakdown */}
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <div className="text-[10px] text-slate-500 mb-1">By Ship Point:</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(info.shipPoints).sort((a, b) => b[1] - a[1]).map(([sp, spCases]) => {
                      const spColor = SHIP_POINT_COLORS[sp] || SHIP_POINT_COLORS.default;
                      return (
                        <span
                          key={sp}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                          style={{ backgroundColor: spColor.bg + '20', color: spColor.bg }}
                        >
                          <span style={{ color: spColor.bg }}>●</span>
                          {sp}: {spCases.toLocaleString()}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  className="mt-3 w-full px-2 py-1.5 bg-blue-600 text-white rounded text-[11px] font-medium hover:bg-blue-700 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStateClick && onStateClick(selectedState === code ? null : code);
                  }}
                >
                  {selectedState === code ? 'Clear Filter' : 'Filter by State'}
                </button>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

// ============================================================================
// TRANSIT MAP COMPONENT (Leaflet-based for Transit Time tab)
// ============================================================================
const TransitMap = ({ origins, destinations, routes, selectedDest, onDestClick }) => {
  // Create origin marker (circle)
  const createOriginIcon = (origin) => {
    return L.divIcon({
      className: 'transit-origin-marker',
      html: `<div style="
        width: 32px;
        height: 32px;
        background: ${origin.color};
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 9px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">${origin.name}</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  // Create destination marker (square)
  const createDestIcon = (dest, isSelected) => {
    const typeColors = { DC: '#0891b2', CP: '#7c3aed', Store: '#ea580c' };
    const color = typeColors[dest.type] || '#64748b';
    const size = isSelected ? 28 : 22;
    return L.divIcon({
      className: 'transit-dest-marker',
      html: `<div style="
        width: ${size}px;
        height: ${size}px;
        background: white;
        border: 3px solid ${color};
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: ${isSelected ? '0 0 0 3px ' + color + '40, ' : ''}0 2px 6px rgba(0,0,0,0.2);
        cursor: pointer;
      "></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  // Get routes to selected destination
  const activeRoutes = selectedDest ? routes.filter(r => r.destId === selectedDest) : [];

  return (
    <MapContainer
      center={[39.8283, -98.5795]}
      zoom={4}
      style={{ height: '300px', width: '100%', borderRadius: '8px' }}
      scrollWheelZoom={true}
      dragging={true}
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      <ZoomControl position="topright" />

      {/* Draw route lines when destination is selected */}
      {activeRoutes.map((route, idx) => {
        const origin = origins[route.origin];
        const dest = destinations.find(d => d.id === route.destId);
        if (!origin || !dest) return null;
        const lineColor = route.mode === 'Rail' ? '#f59e0b' : route.mode === 'Fleet' ? '#22c55e' : '#3b82f6';
        return (
          <Polyline
            key={`route-${idx}`}
            positions={[[origin.lat, origin.lng], [dest.lat, dest.lng]]}
            color={lineColor}
            weight={4}
            opacity={0.8}
            dashArray={route.mode === 'Rail' ? '10, 10' : null}
          />
        );
      })}

      {/* Origin markers */}
      {Object.entries(origins).map(([code, origin]) => (
        <Marker
          key={`origin-${code}`}
          position={[origin.lat, origin.lng]}
          icon={createOriginIcon(origin)}
        >
          <Popup>
            <div className="text-xs">
              <div className="font-bold text-slate-800">{origin.fullName || origin.name}</div>
              <div className="text-slate-600">{origin.city}</div>
              {origin.orderCount && (
                <div className="text-blue-600 font-medium mt-1">{origin.orderCount} orders</div>
              )}
              <div className="text-blue-600 mt-1">Origin Warehouse</div>
            </div>
          </Popup>
        </Marker>
      ))}

      {/* Destination markers */}
      {destinations.map((dest) => {
        const isSelected = selectedDest === dest.id;
        return (
          <Marker
            key={`dest-${dest.id}`}
            position={[dest.lat, dest.lng]}
            icon={createDestIcon(dest, isSelected)}
            eventHandlers={{
              click: () => onDestClick && onDestClick(isSelected ? null : dest.id)
            }}
          >
            <Popup>
              <div className="text-xs min-w-[140px]">
                <div className="font-bold text-slate-800">{dest.name}</div>
                <div className="text-slate-600">{dest.city}</div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                  dest.type === 'DC' ? 'bg-cyan-100 text-cyan-700' :
                  dest.type === 'CP' ? 'bg-purple-100 text-purple-700' :
                  'bg-orange-100 text-orange-700'
                }`}>{dest.type}</span>
                {/* Show routes to this destination */}
                {routes.filter(r => r.destId === dest.id).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="text-[10px] text-slate-500 mb-1">Routes:</div>
                    {routes.filter(r => r.destId === dest.id).map((r, i) => (
                      <div key={i} className="text-[10px] text-slate-600">
                        {r.origin} → {r.days}d ({r.mode})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

// ============================================================================
// SUMMARY CARD COMPONENT (matching Orders style)
// ============================================================================
const SummaryCard = ({ title, value, count, subtitle, description, color, onClick, active }) => {
  const bgColors = {
    blue: 'bg-gradient-to-br from-blue-50 to-white border-blue-200',
    red: 'bg-gradient-to-br from-red-50 to-white border-red-200',
    purple: 'bg-gradient-to-br from-purple-50 to-white border-purple-200',
    green: 'bg-gradient-to-br from-green-50 to-white border-green-200',
    amber: 'bg-gradient-to-br from-amber-50 to-white border-amber-200',
    slate: 'bg-gradient-to-br from-slate-50 to-white border-slate-200',
  };

  const textColors = {
    blue: 'text-blue-600',
    red: 'text-red-600',
    purple: 'text-purple-600',
    green: 'text-green-600',
    amber: 'text-amber-600',
    slate: 'text-slate-600',
  };

  const dotColors = {
    blue: 'bg-blue-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    slate: 'bg-slate-500',
  };

  return (
    <div
      onClick={onClick}
      className={`px-3 py-2.5 rounded-xl border shadow-sm transition-all ${bgColors[color]} ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-2 h-2 rounded-full ${dotColors[color]}`} />
        <p className="text-xs font-medium text-slate-600">{title}</p>
        {description && (
          <span className="relative group">
            <span className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[9px] cursor-help">?</span>
            <span className="absolute left-0 top-5 z-20 hidden group-hover:block w-44 p-2 text-[10px] font-normal text-slate-600 bg-white border border-slate-200 rounded-lg shadow-lg">
              {description}
            </span>
          </span>
        )}
      </div>
      <p className={`text-lg font-bold ${textColors[color]}`}>{value}</p>
      {count !== undefined && <p className="text-xs text-slate-500">{count} orders</p>}
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === 0) return '$0';
  const num = Number(value) || 0;
  if (num === 0) return '$0';
  const thousands = num / 1000;
  if (Math.abs(thousands) >= 1) {
    return `$${thousands.toFixed(1)}K`;
  }
  return `$${num.toFixed(0)}`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function RoutingDashboard({ tenantId = 'demo', userProfile, onSignOut }) {
  // Permission checks based on userProfile.permissions
  const permissions = userProfile?.permissions || {};
  const isPlatformOwner = userProfile?.role === 'platform_owner';
  const isAdmin = userProfile?.role === 'admin';

  // Use permissions object, with fallback for platform owner/admin
  const canViewSettings = isPlatformOwner || isAdmin || permissions.viewSettings;
  const canManageUsers = isPlatformOwner || isAdmin || permissions.manageUsers;
  const canManageShipPoints = isPlatformOwner || isAdmin || permissions.manageShipPoints;
  const canUploadData = isPlatformOwner || isAdmin || permissions.uploadData;
  const canEditNotes = isPlatformOwner || isAdmin || permissions.editNotes;

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null); // 'scheduled', 'notRouted', 'prepaid'
  const [filterWarehouse, setFilterWarehouse] = useState(null);
  const [searchText, setSearchText] = useState('');

  // PO Information table filters
  const [poFilterStatus, setPoFilterStatus] = useState(null);
  const [poFilterMode, setPoFilterMode] = useState(null);
  const [poFilterAlert, setPoFilterAlert] = useState(null);

  // Map destination filter
  const [filterDestState, setFilterDestState] = useState(null);

  // Cross-filter states (Power BI style)
  const [filterMABD, setFilterMABD] = useState(null);
  const [filterShipDate, setFilterShipDate] = useState(null);  // Ship date filter (MM/DD format or 'Confirmed' for no-ship-date orders)
  const [filterPO, setFilterPO] = useState(null);
  const [filterPickWindow, setFilterPickWindow] = useState(null); // 'today', 'tomorrow', 'thisWeek', 'overdue'

  // B2 view toggle: 'pickWindow' or 'confirmedMatrix'
  const [b2View, setB2View] = useState('pickWindow');

  // Global display mode: 'cases' or 'dollars' (dollars only when ERP data available)
  const [displayMode, setDisplayMode] = useState('cases');

  // Get tenant-specific schemas
  const routingSchemas = useMemo(() => getAllRoutingSchemas(tenantId, FirebaseService.getProjectId()), [tenantId]);

  // Initialize tenant context on mount
  useEffect(() => {
    RoutingDataService.setTenant(tenantId);
    RoutingNotesService.setTenant(tenantId);
    DocumentsService.setTenant(tenantId);
    console.log(`🏢 RoutingDashboard initialized for tenant: ${tenantId}`);
  }, [tenantId]);

  // Subscribe to documents from Firestore
  useEffect(() => {
    let unsubscribe = () => {};

    const setupDocumentsSubscription = async () => {
      try {
        setDocumentsLoading(true);
        await DocumentsService.initialize();

        unsubscribe = DocumentsService.subscribeToDocuments((docs) => {
          setDocuments(docs);
          setDocumentsLoading(false);
        });
      } catch (error) {
        console.error('Failed to setup documents subscription:', error);
        setDocumentsLoading(false);
      }
    };

    setupDocumentsSubscription();
    return () => unsubscribe();
  }, [tenantId]);

  // Subscribe to routing notes from Firestore
  useEffect(() => {
    let unsubscribe = () => {};

    const setupNotesSubscription = async () => {
      try {
        setNotesLoading(true);
        await RoutingNotesService.initialize();

        unsubscribe = RoutingNotesService.subscribeToNotes((notes) => {
          // Build lookup by po_number
          const lookup = {};
          notes.forEach(note => {
            if (note.po_number) {
              // Keep first (most recent) note for each PO
              if (!lookup[note.po_number]) {
                lookup[note.po_number] = note;
              }
            }
          });
          setRoutingNotes(lookup);
          setRoutingNotesList(notes);
          setNotesLoading(false);
        });
      } catch (error) {
        console.error('Failed to setup notes subscription:', error);
        setNotesLoading(false);
      }
    };

    setupNotesSubscription();
    return () => unsubscribe();
  }, [tenantId]);

  // Subscribe to ship points from Firestore
  useEffect(() => {
    let unsubscribe = () => {};

    const setupShipPointsSubscription = async () => {
      try {
        setShipPointsLoading(true);
        ShipPointsService.setTenant(tenantId);
        await ShipPointsService.initialize();

        unsubscribe = ShipPointsService.subscribeToShipPoints((points) => {
          setShipPoints(points);
          setShipPointsLoading(false);
        });
      } catch (error) {
        console.error('Failed to setup ship points subscription:', error);
        setShipPointsLoading(false);
      }
    };

    setupShipPointsSubscription();
    return () => unsubscribe();
  }, [tenantId]);

  // CHA-97 / PR-C: tenant users fetched via listUsers callable (one-shot).
  // The pre-PR-C onSnapshot real-time subscription is gone — real-time
  // cross-admin updates are not supported post-rules-lockdown. Click
  // "Refresh users" to re-fetch. After-mutation `refreshUsers()` calls
  // below keep the table fresh within the same admin's session.
  //
  // CodeRabbit M-2 race-guard: track current request sequence to
  // discard stale responses if `tenantId` changes mid-fetch. Without
  // this, a slow response for tenant A can land after the dashboard
  // has switched to tenant B and overwrite tenant B's state.
  const usersRequestSeqRef = useRef(0);

  const refreshUsers = useCallback(async () => {
    const seq = ++usersRequestSeqRef.current;
    try {
      setUsersLoading(true);
      TenantUsersService.setTenant(tenantId);
      await TenantUsersService.initialize();
      const users = await TenantUsersService.getTenantUsers();
      if (seq === usersRequestSeqRef.current) {
        setTenantUsers(users);
      }
    } catch (error) {
      console.error('Failed to refresh users:', error);
    } finally {
      if (seq === usersRequestSeqRef.current) {
        setUsersLoading(false);
      }
    }
  }, [tenantId]);

  // Initial users fetch + tenant config load. Uses the same seq guard
  // so that a stale tenantConfig from a prior tenant can't overwrite
  // the current one mid-switch.
  useEffect(() => {
    const seq = ++usersRequestSeqRef.current;
    const setupUsers = async () => {
      try {
        TenantUsersService.setTenant(tenantId);
        await TenantUsersService.initialize();
        const config = await TenantUsersService.getTenantConfig();
        if (seq !== usersRequestSeqRef.current) return;
        setTenantConfig(config);
        await refreshUsers();
      } catch (error) {
        console.error('Failed to load tenant users:', error);
        if (seq === usersRequestSeqRef.current) {
          setUsersLoading(false);
        }
      }
    };
    setupUsers();
  }, [tenantId, refreshUsers]);

  // Notes state
  const [notesSearch, setNotesSearch] = useState({ type: 'po', value: '' });

  // Routing Notes from Firestore (keyed by po_number for quick lookup)
  const [routingNotes, setRoutingNotes] = useState({});
  const [routingNotesList, setRoutingNotesList] = useState([]); // For Notes tab list
  const [notesLoading, setNotesLoading] = useState(true);

  // Note Modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteModalOrder, setNoteModalOrder] = useState(null);
  const [noteModalExisting, setNoteModalExisting] = useState(null);

  // Documents state (Documents tab - BOL/POD OCR)
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentFilter, setDocumentFilter] = useState('all'); // 'all', 'matched', 'unmatched', 'processing', 'error'
  const [documentSearch, setDocumentSearch] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [manualMatchModal, setManualMatchModal] = useState({ open: false, document: null });

  // Ship Points state (Settings tab)
  const [shipPoints, setShipPoints] = useState([]);
  const [shipPointsLoading, setShipPointsLoading] = useState(true);
  const [shipPointModalOpen, setShipPointModalOpen] = useState(false);
  const [editingShipPoint, setEditingShipPoint] = useState(null);
  const [shipPointForm, setShipPointForm] = useState({
    code: '', location: '', name: '', address: '', city: '', state: '', zip: '', active: true
  });

  // Users state (Settings tab)
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantConfig, setTenantConfig] = useState({ userCap: 5 });
  const [usersLoading, setUsersLoading] = useState(true);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '', name: '', role: 'viewer', tempPassword: ''
  });

  // Filter mode: 'shipPoint' or 'location'
  const [filterMode, setFilterMode] = useState('shipPoint');

  // Transit map state
  const [selectedTransitDest, setSelectedTransitDest] = useState(null);

  // MABD Calculator state
  const [mabdCalcDest, setMabdCalcDest] = useState('');
  const [mabdCalcShipDate, setMabdCalcShipDate] = useState('');
  const [mabdCalcResults, setMabdCalcResults] = useState(null);
  const [mabdCalcLoading, setMabdCalcLoading] = useState(false);

  // Federal holidays for 2025-2026 (for MABD calculation)
  const federalHolidays = useMemo(() => [
    '2025-01-01', '2025-01-20', '2025-02-17', '2025-05-26', '2025-07-04',
    '2025-09-01', '2025-10-13', '2025-11-11', '2025-11-27', '2025-12-25',
    '2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25', '2026-07-03',
    '2026-09-07', '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25',
  ], []);

  // Calculate MABD function
  const calculateMABD = () => {
    if (!mabdCalcDest || !mabdCalcShipDate) {
      alert('Please select a destination and ship date');
      return;
    }

    setMabdCalcLoading(true);

    // Get active ship points with valid coordinates
    const activeOrigins = shipPoints.filter(sp =>
      sp.active && LOCATION_COORDINATES[sp.location?.toUpperCase()]
    );

    if (activeOrigins.length === 0) {
      setMabdCalcResults({ error: 'No ship points configured. Add ship points in Settings.' });
      setMabdCalcLoading(false);
      return;
    }

    // Helper: add business days (excluding weekends and holidays)
    const addBusinessDays = (startDate, days) => {
      let current = new Date(startDate);
      let added = 0;
      while (added < days) {
        current.setDate(current.getDate() + 1);
        const dayOfWeek = current.getDay();
        const dateStr = current.toISOString().split('T')[0];
        // Skip weekends (0=Sunday, 6=Saturday) and holidays
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !federalHolidays.includes(dateStr)) {
          added++;
        }
      }
      return current;
    };

    // Calculate MABD for each origin
    const shipDate = new Date(mabdCalcShipDate);
    const results = activeOrigins.map((sp, idx) => {
      // Simulate transit days based on mode (in real app, this would come from actual route data)
      const modes = ['TL', 'Rail', 'Fleet'];
      const mode = modes[idx % modes.length];
      const baseDays = mode === 'Rail' ? 5 : mode === 'Fleet' ? 3 : 2;
      const transitDays = baseDays + Math.floor(Math.random() * 2); // Add some variance

      const mabdDate = addBusinessDays(shipDate, transitDays);

      return {
        origin: sp.code,
        originName: sp.name || sp.code,
        city: sp.city || sp.location,
        mode,
        transitDays,
        mabd: mabdDate,
        mabdFormatted: mabdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      };
    });

    // Sort by earliest MABD
    results.sort((a, b) => a.mabd - b.mabd);

    // Mark best option
    if (results.length > 0) {
      results[0].isBest = true;
    }

    setMabdCalcResults({ results, shipDate: mabdCalcShipDate, destination: mabdCalcDest });
    setMabdCalcLoading(false);
  };

  const [searchedOrder, setSearchedOrder] = useState(null);
  const [noteForm, setNoteForm] = useState({
    lookup_type: 'po', lookup_value: '', mabd: '',
    original_appt_date: '', override_appt_date: '',
    reason: '', responsibility: '', note: '',
    ticket_number: '', ticket_sent: false
  });

  // Upload status tracking for Data Workflow
  const [uploadStatus, setUploadStatus] = useState({
    erp_orders: { lastUpload: null, rowCount: null, status: 'empty' },
    supplier_one: { lastUpload: null, rowCount: null, status: 'empty' },
    warehouse_logs: { lastUpload: null, rowCount: null, status: 'empty' },
    walmart_tms: { lastUpload: null, rowCount: null, status: 'empty' },
    store_data: { lastUpload: null, rowCount: null, status: 'empty' },
    transit_time: { lastUpload: null, rowCount: null, status: 'empty' },
  });

  // 🆕 Real routing data from BigQuery
  const [routingData, setRoutingData] = useState({
    orders: [],
    summary: { totalOrders: 0, uniquePOs: 0, uniqueLoads: 0, totalCases: 0, scheduledCount: 0, confirmedCount: 0, pendingCount: 0 },
    apptMatrix: { rows: [], columns: [] },
    confirmedMatrix: { rows: [], columns: [] },
    warehouses: [],
    uploadDate: null,
    isLoading: true,
    error: null
  });

  // 🆕 Fetch routing data from BigQuery
  const fetchRoutingData = async () => {
    setRoutingData(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const functions = getFunctions();
      const getRoutingDashboardData = httpsCallable(functions, 'getRoutingDashboardData');
      const result = await getRoutingDashboardData({ tenantId });

      if (result.data.success) {
        setRoutingData({
          orders: result.data.orders || [],
          summary: result.data.summary || {},
          warehouses: result.data.warehouses || [],
          apptMatrix: result.data.apptMatrix || { rows: [], columns: [] },
          confirmedMatrix: result.data.confirmedMatrix || { rows: [], columns: [] },
          uploadDate: result.data.uploadDate,
          isLoading: false,
          error: null
        });

        // Update upload status if we have data
        if (result.data.uploadDate) {
          setUploadStatus(prev => ({
            ...prev,
            walmart_tms: {
              lastUpload: result.data.uploadDate,
              rowCount: result.data.summary?.totalOrders || 0,
              status: 'active'
            }
          }));
        }

        console.log(`✅ Loaded ${result.data.orders?.length || 0} routing orders`);
      } else {
        throw new Error(result.data.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('❌ Failed to fetch routing data:', error);
      setRoutingData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to load routing data'
      }));
    }
  };

  // 🆕 Fetch data on mount and when tenantId changes
  useEffect(() => {
    fetchRoutingData();
  }, [tenantId]);

  // Reset displayMode to cases if no dollar data available
  useEffect(() => {
    const hasDollarData = parseFloat(safeValue(routingData.summary?.totalDollars)) > 0;
    if (!hasDollarData && displayMode === 'dollars') {
      setDisplayMode('cases');
    }
  }, [routingData.summary, displayMode]);

  // Extraction state
  const [extractionStatus, setExtractionStatus] = useState({
    loading: false,
    lastRun: null,
    results: null,
    error: null
  });

  // Handle extraction run
  const handleRunExtraction = async () => {
    setExtractionStatus(prev => ({ ...prev, loading: true, error: null }));
    try {
      const functions = getFunctions();
      const runExtraction = httpsCallable(functions, `runExtraction_${tenantId}`);
      const result = await runExtraction({ runAll: true });

      setExtractionStatus({
        loading: false,
        lastRun: new Date().toISOString(),
        results: result.data,
        error: null
      });
      console.log('Extraction results:', result.data);

      // Update upload status with actual row counts from extraction
      if (result.data?.results) {
        const today = new Date().toISOString().split('T')[0];
        setUploadStatus(prev => {
          const updated = { ...prev };
          // Map extraction table names to upload status keys
          const tableMapping = {
            routing_walmart_tms: 'walmart_tms',
            routing_store_data: 'store_data',
            routing_transit_time: 'transit_time'
          };
          Object.entries(result.data.results).forEach(([table, tableResult]) => {
            const statusKey = tableMapping[table];
            if (statusKey && tableResult.success && tableResult.rowCount) {
              updated[statusKey] = {
                ...updated[statusKey],
                rowCount: tableResult.rowCount,
                lastUpload: today,
                status: 'active'
              };
            }
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Extraction error:', error);
      setExtractionStatus(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };


  // Handle upload complete
  const handleUploadComplete = (fileType, result) => {
    console.log(`✅ Upload complete for ${fileType}:`, result);
    setUploadStatus(prev => ({
      ...prev,
      [fileType]: {
        lastUpload: new Date().toISOString().split('T')[0],
        rowCount: result.rowCount > 0 ? result.rowCount : 'Uploaded',
        status: 'active'
      }
    }));
  };

  // Handle upload error
  const handleUploadError = (fileType, error) => {
    console.error(`❌ Upload error for ${fileType}:`, error);
  };

  // Build matrix with override dates applied from notes
  // This replaces the BigQuery matrix when overrides exist
  const apptMatrixWithOverrides = useMemo(() => {
    const orders = routingData.orders || [];
    if (orders.length === 0) {
      return { rows: [], rowsDollars: [], columns: [] };
    }

    // Build matrix: MABD (rows) vs Ship Date (columns)
    // Ship Date = override_pickup_date from notes OR carrier_pu_date from order
    const matrixData = {}; // { mabd: { shipDate: { cases, dollars } } }
    const allShipDates = new Set();
    const allMABDs = new Set();

    orders.forEach(order => {
      const poNum = safeValue(order.po_number);
      const note = routingNotes[poNum];
      const status = safeValue(order.routing_status);

      // Get ship date - use override if exists, otherwise original
      let shipDate;
      if (note?.override_pickup_date) {
        shipDate = note.override_pickup_date;
      } else if (status === 'Scheduled') {
        shipDate = safeValue(order.carrier_pu_date);
      } else if (status === 'Confirmed') {
        shipDate = 'Confirmed';
      } else {
        shipDate = 'Pending';
      }

      const mabd = safeValue(order.mabd) || 'No MABD';
      const cases = parseInt(safeValue(order.cases)) || 0;
      const dollars = parseFloat(safeValue(order.po_amount)) || 0;

      allShipDates.add(shipDate);
      allMABDs.add(mabd);

      if (!matrixData[mabd]) {
        matrixData[mabd] = {};
      }
      if (!matrixData[mabd][shipDate]) {
        matrixData[mabd][shipDate] = { cases: 0, dollars: 0 };
      }
      matrixData[mabd][shipDate].cases += cases;
      matrixData[mabd][shipDate].dollars += dollars;
    });

    // Sort columns: Pending, Confirmed first, then dates sorted
    const sortedShipDates = Array.from(allShipDates).sort((a, b) => {
      if (a === 'Pending') return -1;
      if (b === 'Pending') return 1;
      if (a === 'Confirmed') return -1;
      if (b === 'Confirmed') return 1;
      // Parse MM/DD dates for sorting
      const parseDate = (d) => {
        const parts = d.split('/');
        if (parts.length === 2) {
          return new Date(2026, parseInt(parts[0]) - 1, parseInt(parts[1]));
        }
        return new Date(d);
      };
      return parseDate(a) - parseDate(b);
    });

    // Sort MABDs
    const sortedMABDs = Array.from(allMABDs).sort((a, b) => {
      if (a === 'No MABD') return 1;
      if (b === 'No MABD') return -1;
      const parseDate = (d) => {
        const parts = d.split('/');
        if (parts.length === 2) {
          return new Date(2026, parseInt(parts[0]) - 1, parseInt(parts[1]));
        }
        return new Date(d);
      };
      return parseDate(a) - parseDate(b);
    });

    // Build rows for cases
    const rows = sortedMABDs.map(mabd => {
      const row = { mabd };
      let total = 0;
      sortedShipDates.forEach(shipDate => {
        const val = matrixData[mabd]?.[shipDate]?.cases || 0;
        row[shipDate] = val;
        total += val;
      });
      row.total = total;
      return row;
    });

    // Build rows for dollars
    const rowsDollars = sortedMABDs.map(mabd => {
      const row = { mabd };
      let total = 0;
      sortedShipDates.forEach(shipDate => {
        const val = matrixData[mabd]?.[shipDate]?.dollars || 0;
        row[shipDate] = val;
        total += val;
      });
      row.total = total;
      return row;
    });

    return { rows, rowsDollars, columns: sortedShipDates };
  }, [routingData.orders, routingNotes]);

  // Use override-aware matrix (falls back to BigQuery matrix if no orders)
  const apptMatrixRaw = useMemo(() => {
    // Use locally computed matrix with overrides
    if (apptMatrixWithOverrides.rows.length > 0) {
      return apptMatrixWithOverrides;
    }
    // Fallback to BigQuery matrix
    return routingData.apptMatrix || { rows: [], rowsDollars: [], columns: [] };
  }, [apptMatrixWithOverrides, routingData.apptMatrix]);

  // Get the right rows based on display mode
  const apptMatrix = useMemo(() => ({
    rows: displayMode === 'cases' ? apptMatrixRaw.rows : (apptMatrixRaw.rowsDollars || apptMatrixRaw.rows),
    columns: apptMatrixRaw.columns
  }), [apptMatrixRaw, displayMode]);

  // Build confirmed matrix with overrides (only Confirmed status orders)
  const confirmedMatrixWithOverrides = useMemo(() => {
    const orders = (routingData.orders || []).filter(o => safeValue(o.routing_status) === 'Confirmed');
    if (orders.length === 0) {
      return { rows: [], rowsDollars: [], columns: [] };
    }

    // Build matrix: MABD (rows) vs Pickup Window Start (columns)
    // If note has override, use that as the ship date column instead
    const matrixData = {};
    const allColumns = new Set();
    const allMABDs = new Set();

    orders.forEach(order => {
      const poNum = safeValue(order.po_number);
      const note = routingNotes[poNum];

      // Column = override date if exists, else pickup window start
      let colDate;
      if (note?.override_pickup_date) {
        colDate = note.override_pickup_date;
      } else {
        colDate = formatShortDate(safeValue(order.pickup_window_start)) || 'No Window';
      }

      const mabd = safeValue(order.mabd) || 'No MABD';
      const cases = parseInt(safeValue(order.cases)) || 0;
      const dollars = parseFloat(safeValue(order.po_amount)) || 0;

      allColumns.add(colDate);
      allMABDs.add(mabd);

      if (!matrixData[mabd]) {
        matrixData[mabd] = {};
      }
      if (!matrixData[mabd][colDate]) {
        matrixData[mabd][colDate] = { cases: 0, dollars: 0 };
      }
      matrixData[mabd][colDate].cases += cases;
      matrixData[mabd][colDate].dollars += dollars;
    });

    // Sort columns
    const sortedColumns = Array.from(allColumns).sort((a, b) => {
      if (a === 'No Window') return 1;
      if (b === 'No Window') return -1;
      const parseDate = (d) => {
        const parts = d.split('/');
        if (parts.length === 2) {
          return new Date(2026, parseInt(parts[0]) - 1, parseInt(parts[1]));
        }
        return new Date(d);
      };
      return parseDate(a) - parseDate(b);
    });

    // Sort MABDs
    const sortedMABDs = Array.from(allMABDs).sort((a, b) => {
      if (a === 'No MABD') return 1;
      if (b === 'No MABD') return -1;
      const parseDate = (d) => {
        const parts = d.split('/');
        if (parts.length === 2) {
          return new Date(2026, parseInt(parts[0]) - 1, parseInt(parts[1]));
        }
        return new Date(d);
      };
      return parseDate(a) - parseDate(b);
    });

    // Build rows
    const rows = sortedMABDs.map(mabd => {
      const row = { mabd };
      let total = 0;
      sortedColumns.forEach(col => {
        const val = matrixData[mabd]?.[col]?.cases || 0;
        row[col] = val;
        total += val;
      });
      row.total = total;
      return row;
    });

    const rowsDollars = sortedMABDs.map(mabd => {
      const row = { mabd };
      let total = 0;
      sortedColumns.forEach(col => {
        const val = matrixData[mabd]?.[col]?.dollars || 0;
        row[col] = val;
        total += val;
      });
      row.total = total;
      return row;
    });

    return { rows, rowsDollars, columns: sortedColumns };
  }, [routingData.orders, routingNotes]);

  // Use override-aware confirmed matrix
  const confirmedMatrixRaw = useMemo(() => {
    if (confirmedMatrixWithOverrides.rows.length > 0) {
      return confirmedMatrixWithOverrides;
    }
    return routingData.confirmedMatrix || { rows: [], rowsDollars: [], columns: [] };
  }, [confirmedMatrixWithOverrides, routingData.confirmedMatrix]);

  // Get the right rows based on display mode
  const confirmedMatrix = useMemo(() => ({
    rows: displayMode === 'cases' ? confirmedMatrixRaw.rows : (confirmedMatrixRaw.rowsDollars || confirmedMatrixRaw.rows),
    columns: confirmedMatrixRaw.columns
  }), [confirmedMatrixRaw, displayMode]);

  // Summary metrics from API - already calculated in Cloud Function
  const summaryMetrics = useMemo(() => {
    const summary = routingData.summary || {};
    const scheduledCount = parseInt(safeValue(summary.scheduledCount)) || 0;
    const confirmedCount = parseInt(safeValue(summary.confirmedCount)) || 0;
    const pendingCount = parseInt(safeValue(summary.pendingCount)) || 0;
    // Use actual cases per status from BigQuery
    const scheduledCases = parseInt(safeValue(summary.scheduledCases)) || 0;
    const confirmedCases = parseInt(safeValue(summary.confirmedCases)) || 0;
    const pendingCases = parseInt(safeValue(summary.pendingCases)) || 0;
    // Dollar amounts
    const scheduledDollars = parseFloat(safeValue(summary.scheduledDollars)) || 0;
    const confirmedDollars = parseFloat(safeValue(summary.confirmedDollars)) || 0;
    const pendingDollars = parseFloat(safeValue(summary.pendingDollars)) || 0;
    const totalDollars = parseFloat(safeValue(summary.totalDollars)) || 0;

    return {
      scheduled: { total: scheduledCases, dollars: scheduledDollars, count: scheduledCount },
      confirmed: { total: confirmedCases, dollars: confirmedDollars, count: confirmedCount },
      pending: { total: pendingCases, dollars: pendingDollars, count: pendingCount },
      totalDollars
    };
  }, [routingData.summary]);

  // Build ship point to location lookup
  const shipPointLocationLookup = useMemo(() => {
    const lookup = {};
    shipPoints.forEach(sp => {
      if (sp.code && sp.location) {
        lookup[sp.code] = sp.location;
      }
    });
    return lookup;
  }, [shipPoints]);

  // Filtered orders (using real BigQuery data field names)
  // All filters are applied cumulatively (AND logic)
  const filteredOrders = useMemo(() => {
    return routingData.orders.filter(order => {
      // Warehouse/Location filter
      const shipPoint = safeValue(order.ship_point);
      if (filterWarehouse) {
        if (filterMode === 'location') {
          // In location mode, check if the ship point's location matches
          const orderLocation = shipPointLocationLookup[shipPoint] || shipPoint;
          if (orderLocation !== filterWarehouse) return false;
        } else {
          // In ship point mode, direct match
          if (shipPoint !== filterWarehouse) return false;
        }
      }

      // Search filter
      if (searchText) {
        const search = searchText.toLowerCase();
        const poNum = String(safeValue(order.po_number) || '').toLowerCase();
        const loadId = String(safeValue(order.load_id) || '').toLowerCase();
        const carrier = String(safeValue(order.carrier_name) || '').toLowerCase();
        if (!poNum.includes(search) && !loadId.includes(search) && !carrier.includes(search)) return false;
      }

      // Status filters (summary cards)
      const status = safeValue(order.routing_status);
      if (selectedFilter === 'scheduled' && status !== 'Scheduled') return false;
      if (selectedFilter === 'confirmed' && status !== 'Confirmed') return false;
      if (selectedFilter === 'pending' && status !== 'Pending') return false;

      // PO table dropdown filters
      if (poFilterStatus && status !== poFilterStatus) return false;
      const mode = safeValue(order.mode);
      if (poFilterMode && mode !== poFilterMode) return false;
      const alert = safeValue(order.status_flag);
      if (poFilterAlert && alert !== poFilterAlert) return false;

      // Map destination filter
      if (filterDestState) {
        const destState = safeValue(order.store_state) || safeValue(order.dc_state);
        if (destState !== filterDestState) return false;
      }

      // Cross-filters from matrix tables
      if (filterMABD) {
        const mabd = safeValue(order.mabd);
        // Handle 'No MABD' special case (matrix shows 'No MABD' for null values)
        if (filterMABD === 'No MABD') {
          if (mabd !== null) return false;
        } else {
          if (mabd !== filterMABD) return false;
        }
      }
      if (filterShipDate) {
        const routingStatus = safeValue(order.routing_status);
        const poNum = safeValue(order.po_number);
        const note = routingNotes[poNum];

        // Get effective ship date (override if exists, else original)
        let effectiveShipDate;
        if (note?.override_pickup_date) {
          effectiveShipDate = note.override_pickup_date;
        } else if (routingStatus === 'Scheduled') {
          effectiveShipDate = safeValue(order.carrier_pu_date);
        } else if (routingStatus === 'Confirmed') {
          effectiveShipDate = 'Confirmed';
        } else {
          effectiveShipDate = 'Pending';
        }

        // Filter by effective ship date
        if (effectiveShipDate !== filterShipDate) return false;
      }

      // PO filter from clicking a row
      if (filterPO) {
        const po = safeValue(order.po_number);
        if (po !== filterPO) return false;
      }

      // Pick window filter (Today, Tomorrow, This Week, Overdue)
      if (filterPickWindow) {
        const status = safeValue(order.routing_status);
        // Only apply to non-scheduled orders
        if (status === 'Scheduled') return false;

        const pickupStart = safeValue(order.pickup_window_start);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);

        if (!pickupStart) {
          // No pickup window - only match "thisWeek"
          if (filterPickWindow !== 'thisWeek') return false;
        } else {
          const pickupDate = new Date(pickupStart);
          if (isNaN(pickupDate.getTime())) {
            if (filterPickWindow !== 'thisWeek') return false;
          } else {
            pickupDate.setHours(0, 0, 0, 0);
            if (filterPickWindow === 'overdue' && pickupDate >= today) return false;
            if (filterPickWindow === 'today' && pickupDate.getTime() !== today.getTime()) return false;
            if (filterPickWindow === 'tomorrow' && pickupDate.getTime() !== tomorrow.getTime()) return false;
            if (filterPickWindow === 'thisWeek' && (pickupDate < today || pickupDate > weekEnd)) return false;
          }
        }
      }

      return true;
    });
  }, [filterWarehouse, filterMode, shipPointLocationLookup, searchText, selectedFilter, routingData.orders, poFilterStatus, poFilterMode, poFilterAlert, filterDestState, filterMABD, filterShipDate, filterPO, filterPickWindow, routingNotes]);

  // Map filtered orders - applies all filters EXCEPT state filter (so all states remain visible on map)
  const mapFilteredOrders = useMemo(() => {
    return routingData.orders.filter(order => {
      // Warehouse/Location filter
      const shipPoint = safeValue(order.ship_point);
      if (filterWarehouse) {
        if (filterMode === 'location') {
          const orderLocation = shipPointLocationLookup[shipPoint] || shipPoint;
          if (orderLocation !== filterWarehouse) return false;
        } else {
          if (shipPoint !== filterWarehouse) return false;
        }
      }

      // Search filter
      if (searchText) {
        const search = searchText.toLowerCase();
        const poNum = String(safeValue(order.po_number) || '').toLowerCase();
        const loadId = String(safeValue(order.load_id) || '').toLowerCase();
        const carrier = String(safeValue(order.carrier_name) || '').toLowerCase();
        if (!poNum.includes(search) && !loadId.includes(search) && !carrier.includes(search)) return false;
      }

      // Status filters (summary cards)
      const status = safeValue(order.routing_status);
      if (selectedFilter === 'scheduled' && status !== 'Scheduled') return false;
      if (selectedFilter === 'confirmed' && status !== 'Confirmed') return false;
      if (selectedFilter === 'pending' && status !== 'Pending') return false;

      // PO table dropdown filters
      if (poFilterStatus && status !== poFilterStatus) return false;
      const mode = safeValue(order.mode);
      if (poFilterMode && mode !== poFilterMode) return false;
      const alert = safeValue(order.status_flag);
      if (poFilterAlert && alert !== poFilterAlert) return false;

      // Cross-filters from matrix tables
      if (filterMABD) {
        const mabd = safeValue(order.mabd);
        // Handle 'No MABD' special case (matrix shows 'No MABD' for null values)
        if (filterMABD === 'No MABD') {
          if (mabd !== null) return false;
        } else {
          if (mabd !== filterMABD) return false;
        }
      }
      if (filterShipDate) {
        const routingStatus = safeValue(order.routing_status);
        const poNum = safeValue(order.po_number);
        const note = routingNotes[poNum];

        // Get effective ship date (override if exists, else original)
        let effectiveShipDate;
        if (note?.override_pickup_date) {
          effectiveShipDate = note.override_pickup_date;
        } else if (routingStatus === 'Scheduled') {
          effectiveShipDate = safeValue(order.carrier_pu_date);
        } else if (routingStatus === 'Confirmed') {
          effectiveShipDate = 'Confirmed';
        } else {
          effectiveShipDate = 'Pending';
        }

        // Filter by effective ship date
        if (effectiveShipDate !== filterShipDate) return false;
      }

      // PO filter from clicking a row
      if (filterPO) {
        const po = safeValue(order.po_number);
        if (po !== filterPO) return false;
      }

      // Pick window filter (Today, Tomorrow, This Week, Overdue)
      if (filterPickWindow) {
        const status = safeValue(order.routing_status);
        if (status === 'Scheduled') return false;

        const pickupStart = safeValue(order.pickup_window_start);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);

        if (!pickupStart) {
          if (filterPickWindow !== 'thisWeek') return false;
        } else {
          const pickupDate = new Date(pickupStart);
          if (isNaN(pickupDate.getTime())) {
            if (filterPickWindow !== 'thisWeek') return false;
          } else {
            pickupDate.setHours(0, 0, 0, 0);
            if (filterPickWindow === 'overdue' && pickupDate >= today) return false;
            if (filterPickWindow === 'today' && pickupDate.getTime() !== today.getTime()) return false;
            if (filterPickWindow === 'tomorrow' && pickupDate.getTime() !== tomorrow.getTime()) return false;
            if (filterPickWindow === 'thisWeek' && (pickupDate < today || pickupDate > weekEnd)) return false;
          }
        }
      }

      // NOTE: filterDestState intentionally NOT applied here so map shows all states
      return true;
    });
  }, [filterWarehouse, filterMode, shipPointLocationLookup, searchText, selectedFilter, routingData.orders, poFilterStatus, poFilterMode, poFilterAlert, filterMABD, filterShipDate, filterPO, filterPickWindow, routingNotes]);

  // Get unique values for PO table filters
  const poFilterOptions = useMemo(() => {
    const statuses = new Set();
    const modes = new Set();
    const alerts = new Set();
    routingData.orders.forEach(order => {
      const status = safeValue(order.routing_status);
      const mode = safeValue(order.mode);
      const alert = safeValue(order.status_flag);
      if (status && status !== 'nan' && status !== 'NULL') statuses.add(status);
      if (mode && mode !== 'nan' && mode !== 'NULL') modes.add(mode);
      if (alert && alert !== 'nan' && alert !== 'NULL') alerts.add(alert);
    });
    return {
      statuses: Array.from(statuses).sort(),
      modes: Array.from(modes).sort(),
      alerts: Array.from(alerts).sort()
    };
  }, [routingData.orders]);

  // Check if any PO filters are active
  const hasPoFilters = poFilterStatus || poFilterMode || poFilterAlert;

  // Clear all PO table filters
  const clearPoFilters = () => {
    setPoFilterStatus(null);
    setPoFilterMode(null);
    setPoFilterAlert(null);
  };

  // Export PO Information to Excel
  const handleExportPO = () => {
    const exportData = filteredOrders.map(order => ({
      'PO Number': safeValue(order.po_number),
      'Load ID': safeValue(order.load_id),
      'Warehouse': safeValue(order.warehouse),
      'Destination': safeValue(order.po_dest),
      'Carrier': safeValue(order.carrier_name),
      'PO Type': safeValue(order.po_type),
      'Department': safeValue(order.department),
      'Event Code': safeValue(order.event_code),
      'Mode': safeValue(order.mode),
      'Loading Method': safeValue(order.loading_method),
      'Stage': safeValue(order.routing_status),
      'Route Date': safeValue(order.confirmed_date),
      'Ship Date': safeValue(order.carrier_pu_date),
      'Transit Days': safeValue(order.transit_days),
      'ETA': safeValue(order.expected_arrival),
      'MABD': safeValue(order.mabd),
      'Alert': safeValue(order.status_flag),
      'Cases': safeValue(order.cases),
      'Pallets': safeValue(order.pallets)
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PO Information');
    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `PO_Information_${today}.xlsx`);
  };

  // Calculate pick window stats from real data (pending orders only)
  const pickWindowStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);

    const stats = {
      today: { orders: 0, cases: 0 },
      tomorrow: { orders: 0, cases: 0 },
      thisWeek: { orders: 0, cases: 0 },
      overdue: { orders: 0, cases: 0 }
    };

    routingData.orders.forEach(order => {
      const status = safeValue(order.routing_status);
      // Only count pending/confirmed orders (not yet scheduled)
      if (status === 'Scheduled') return;

      const cases = parseInt(safeValue(order.cases)) || 0;
      const pickupStart = safeValue(order.pickup_window_start);

      if (!pickupStart) {
        // No pickup window = pending
        stats.thisWeek.orders++;
        stats.thisWeek.cases += cases;
        return;
      }

      // Parse the pickup date
      const pickupDate = new Date(pickupStart);
      if (isNaN(pickupDate.getTime())) {
        stats.thisWeek.orders++;
        stats.thisWeek.cases += cases;
        return;
      }

      pickupDate.setHours(0, 0, 0, 0);

      if (pickupDate < today) {
        stats.overdue.orders++;
        stats.overdue.cases += cases;
      } else if (pickupDate.getTime() === today.getTime()) {
        stats.today.orders++;
        stats.today.cases += cases;
      } else if (pickupDate.getTime() === tomorrow.getTime()) {
        stats.tomorrow.orders++;
        stats.tomorrow.cases += cases;
      } else if (pickupDate <= weekEnd) {
        stats.thisWeek.orders++;
        stats.thisWeek.cases += cases;
      }
    });

    return stats;
  }, [routingData.orders]);

  // Calculate split shipments (POs with multiple load IDs)
  const splitShipments = useMemo(() => {
    const poGroups = {};

    routingData.orders.forEach(order => {
      const poNum = safeValue(order.po_number);
      const loadId = safeValue(order.load_id);
      const cases = parseInt(safeValue(order.cases)) || 0;
      const shipDate = safeValue(order.carrier_pu_date);

      if (!poNum) return;

      if (!poGroups[poNum]) {
        poGroups[poNum] = { po: poNum, loads: new Set(), cases: 0, shipDates: [] };
      }
      if (loadId) poGroups[poNum].loads.add(loadId);
      poGroups[poNum].cases += cases;
      if (shipDate) poGroups[poNum].shipDates.push(shipDate);
    });

    // Only keep POs with multiple loads (splits)
    return Object.values(poGroups)
      .filter(g => g.loads.size > 1)
      .map(g => {
        // Sort dates to find first and last
        const sortedDates = g.shipDates.sort((a, b) => new Date(a) - new Date(b));
        return {
          po: g.po,
          loadCount: g.loads.size,
          cases: g.cases,
          firstShip: sortedDates[0] || '—',
          lastShip: sortedDates[sortedDates.length - 1] || '—'
        };
      })
      .sort((a, b) => b.loadCount - a.loadCount)
      .slice(0, 10); // Top 10 split shipments
  }, [routingData.orders]);

  // Lookup for split POs (for PO Information table)
  const splitPOLookup = useMemo(() => {
    const lookup = {};
    splitShipments.forEach(s => {
      lookup[s.po] = s.loadCount;
    });
    return lookup;
  }, [splitShipments]);

  const hasActiveFilters = filterWarehouse || searchText || selectedFilter || poFilterStatus || poFilterMode || poFilterAlert || filterDestState || filterMABD || filterShipDate || filterPO || filterPickWindow;

  // Transit map filter state
  const [selectedTransitOrigin, setSelectedTransitOrigin] = useState(null);
  const [selectedTransitMode, setSelectedTransitMode] = useState(null);

  // City/State to coordinates lookup for geocoding
  const CITY_COORDS = {
    // Major US cities
    'Los Angeles, CA': { lat: 34.0522, lng: -118.2437 },
    'Dallas, TX': { lat: 32.7767, lng: -96.7970 },
    'Chicago, IL': { lat: 41.8781, lng: -87.6298 },
    'Atlanta, GA': { lat: 33.7490, lng: -84.3880 },
    'Seattle, WA': { lat: 47.6062, lng: -122.3321 },
    'Denver, CO': { lat: 39.7392, lng: -104.9903 },
    'Phoenix, AZ': { lat: 33.4484, lng: -112.0740 },
    'Miami, FL': { lat: 25.7617, lng: -80.1918 },
    'New York, NY': { lat: 40.7128, lng: -74.0060 },
    'Houston, TX': { lat: 29.7604, lng: -95.3698 },
    'Philadelphia, PA': { lat: 39.9526, lng: -75.1652 },
    'San Antonio, TX': { lat: 29.4241, lng: -98.4936 },
    'San Diego, CA': { lat: 32.7157, lng: -117.1611 },
    'San Jose, CA': { lat: 37.3382, lng: -121.8863 },
    'Austin, TX': { lat: 30.2672, lng: -97.7431 },
    'Jacksonville, FL': { lat: 30.3322, lng: -81.6557 },
    'Fort Worth, TX': { lat: 32.7555, lng: -97.3308 },
    'Columbus, OH': { lat: 39.9612, lng: -82.9988 },
    'Indianapolis, IN': { lat: 39.7684, lng: -86.1581 },
    'Charlotte, NC': { lat: 35.2271, lng: -80.8431 },
    'San Francisco, CA': { lat: 37.7749, lng: -122.4194 },
    'Memphis, TN': { lat: 35.1495, lng: -90.0490 },
    'Boston, MA': { lat: 42.3601, lng: -71.0589 },
    'Baltimore, MD': { lat: 39.2904, lng: -76.6122 },
    'Oklahoma City, OK': { lat: 35.4676, lng: -97.5164 },
    'Las Vegas, NV': { lat: 36.1699, lng: -115.1398 },
    'Portland, OR': { lat: 45.5152, lng: -122.6784 },
    'Louisville, KY': { lat: 38.2527, lng: -85.7585 },
    'Milwaukee, WI': { lat: 43.0389, lng: -87.9065 },
    'Albuquerque, NM': { lat: 35.0844, lng: -106.6504 },
    'Tucson, AZ': { lat: 32.2226, lng: -110.9747 },
    'Fresno, CA': { lat: 36.7378, lng: -119.7871 },
    'Sacramento, CA': { lat: 38.5816, lng: -121.4944 },
    'Kansas City, MO': { lat: 39.0997, lng: -94.5786 },
    'Mesa, AZ': { lat: 33.4152, lng: -111.8315 },
    'Omaha, NE': { lat: 41.2565, lng: -95.9345 },
    'Cleveland, OH': { lat: 41.4993, lng: -81.6944 },
    'Virginia Beach, VA': { lat: 36.8529, lng: -75.9780 },
    'Raleigh, NC': { lat: 35.7796, lng: -78.6382 },
    'Detroit, MI': { lat: 42.3314, lng: -83.0458 },
    'Minneapolis, MN': { lat: 44.9778, lng: -93.2650 },
    'Tampa, FL': { lat: 27.9506, lng: -82.4572 },
    'St. Louis, MO': { lat: 38.6270, lng: -90.1994 },
    'Pittsburgh, PA': { lat: 40.4406, lng: -79.9959 },
    'Cincinnati, OH': { lat: 39.1031, lng: -84.5120 },
    'Orlando, FL': { lat: 28.5383, lng: -81.3792 },
    'New Orleans, LA': { lat: 29.9511, lng: -90.0715 },
    'Bentonville, AR': { lat: 36.3729, lng: -94.2088 },
    // State center fallbacks
    'TX': { lat: 31.9686, lng: -99.9018 },
    'CA': { lat: 36.7783, lng: -119.4179 },
    'FL': { lat: 27.6648, lng: -81.5158 },
    'NY': { lat: 42.1657, lng: -74.9481 },
    'PA': { lat: 41.2033, lng: -77.1945 },
    'IL': { lat: 40.6331, lng: -89.3985 },
    'OH': { lat: 40.4173, lng: -82.9071 },
    'GA': { lat: 32.1656, lng: -82.9001 },
    'NC': { lat: 35.7596, lng: -79.0193 },
    'MI': { lat: 44.3148, lng: -85.6024 },
  };

  // Ship point colors for map
  const ORIGIN_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#10b981', '#f97316', '#6366f1'];

  // Zip code to coordinates lookup (major zip code prefixes)
  const ZIP_COORDS = {
    // CA
    '900': { lat: 34.0522, lng: -118.2437 }, '901': { lat: 34.0522, lng: -118.2437 },
    '902': { lat: 34.0522, lng: -118.2437 }, '903': { lat: 34.0522, lng: -118.2437 },
    '904': { lat: 34.0522, lng: -118.2437 }, '905': { lat: 34.0522, lng: -118.2437 },
    '906': { lat: 34.0522, lng: -118.2437 }, '907': { lat: 34.0522, lng: -118.2437 },
    '908': { lat: 34.0522, lng: -118.2437 }, '910': { lat: 34.1478, lng: -118.1445 },
    '911': { lat: 34.1478, lng: -118.1445 }, '912': { lat: 34.1478, lng: -118.1445 },
    '913': { lat: 34.4208, lng: -118.5425 }, '914': { lat: 34.1808, lng: -118.3090 },
    '917': { lat: 34.0195, lng: -118.4912 }, '920': { lat: 32.7157, lng: -117.1611 },
    '921': { lat: 32.7157, lng: -117.1611 }, '922': { lat: 33.1959, lng: -117.3795 },
    '923': { lat: 33.8366, lng: -117.9143 }, '924': { lat: 33.7455, lng: -117.8677 },
    '925': { lat: 33.8366, lng: -117.9143 }, '926': { lat: 33.6846, lng: -117.8265 },
    '927': { lat: 33.1959, lng: -117.3795 }, '928': { lat: 33.9425, lng: -117.2297 },
    '930': { lat: 34.9530, lng: -120.4357 }, '931': { lat: 34.4208, lng: -119.6982 },
    '932': { lat: 34.2694, lng: -118.7815 }, '933': { lat: 35.3733, lng: -119.0187 },
    '934': { lat: 34.4208, lng: -119.6982 }, '935': { lat: 35.0078, lng: -118.9488 },
    '936': { lat: 36.7378, lng: -119.7871 }, '937': { lat: 36.7378, lng: -119.7871 },
    '940': { lat: 37.7749, lng: -122.4194 }, '941': { lat: 37.7749, lng: -122.4194 },
    '942': { lat: 37.5485, lng: -122.0590 }, '943': { lat: 37.4419, lng: -122.1430 },
    '944': { lat: 37.8044, lng: -122.2712 }, '945': { lat: 37.8044, lng: -122.2712 },
    '946': { lat: 37.8044, lng: -122.2712 }, '947': { lat: 37.8716, lng: -122.2727 },
    '948': { lat: 37.8716, lng: -122.2727 }, '949': { lat: 37.5585, lng: -122.2711 },
    '950': { lat: 37.3382, lng: -121.8863 }, '951': { lat: 33.9806, lng: -117.3755 },
    '952': { lat: 37.9577, lng: -121.2908 }, '953': { lat: 37.9577, lng: -121.2908 },
    '954': { lat: 37.6879, lng: -122.4702 }, '955': { lat: 40.5865, lng: -122.3917 },
    '956': { lat: 38.5816, lng: -121.4944 }, '957': { lat: 38.5816, lng: -121.4944 },
    '958': { lat: 38.5816, lng: -121.4944 }, '959': { lat: 39.5296, lng: -121.5525 },
    // TX
    '750': { lat: 32.7767, lng: -96.7970 }, '751': { lat: 32.7767, lng: -96.7970 },
    '752': { lat: 32.7767, lng: -96.7970 }, '753': { lat: 32.7767, lng: -96.7970 },
    '754': { lat: 33.2148, lng: -97.1331 }, '755': { lat: 33.4539, lng: -94.0425 },
    '756': { lat: 31.5493, lng: -97.1467 }, '757': { lat: 31.5493, lng: -97.1467 },
    '758': { lat: 31.1171, lng: -97.7278 }, '759': { lat: 31.7619, lng: -95.6308 },
    '760': { lat: 32.7555, lng: -97.3308 }, '761': { lat: 32.7555, lng: -97.3308 },
    '762': { lat: 32.7555, lng: -97.3308 }, '763': { lat: 33.1507, lng: -96.8236 },
    '764': { lat: 32.7157, lng: -97.3522 }, '765': { lat: 33.2148, lng: -97.1331 },
    '766': { lat: 33.9137, lng: -98.4934 }, '767': { lat: 33.9137, lng: -98.4934 },
    '768': { lat: 33.5779, lng: -101.8552 }, '769': { lat: 32.4487, lng: -99.7331 },
    '770': { lat: 29.7604, lng: -95.3698 }, '771': { lat: 29.7604, lng: -95.3698 },
    '772': { lat: 29.7604, lng: -95.3698 }, '773': { lat: 29.7604, lng: -95.3698 },
    '774': { lat: 29.7604, lng: -95.3698 }, '775': { lat: 29.7604, lng: -95.3698 },
    '776': { lat: 30.0799, lng: -94.1266 }, '777': { lat: 30.0799, lng: -94.1266 },
    '778': { lat: 27.8006, lng: -97.3964 }, '779': { lat: 28.8053, lng: -96.9930 },
    '780': { lat: 29.4241, lng: -98.4936 }, '781': { lat: 29.4241, lng: -98.4936 },
    '782': { lat: 29.4241, lng: -98.4936 }, '783': { lat: 27.5064, lng: -99.5075 },
    '784': { lat: 27.5064, lng: -99.5075 }, '785': { lat: 26.2034, lng: -98.2300 },
    '786': { lat: 30.2672, lng: -97.7431 }, '787': { lat: 30.2672, lng: -97.7431 },
    '788': { lat: 29.8830, lng: -97.9414 }, '789': { lat: 30.2672, lng: -97.7431 },
    '790': { lat: 35.2220, lng: -101.8313 }, '791': { lat: 35.2220, lng: -101.8313 },
    '792': { lat: 34.1808, lng: -101.8196 }, '793': { lat: 33.5779, lng: -101.8552 },
    '794': { lat: 31.7619, lng: -106.4850 }, '795': { lat: 31.7619, lng: -106.4850 },
    '796': { lat: 31.4638, lng: -100.4370 }, '797': { lat: 31.8457, lng: -102.3676 },
    '798': { lat: 31.7619, lng: -106.4850 }, '799': { lat: 31.7619, lng: -106.4850 },
    // FL
    '320': { lat: 30.3322, lng: -81.6557 }, '321': { lat: 28.5383, lng: -81.3792 },
    '322': { lat: 30.3322, lng: -81.6557 }, '323': { lat: 30.4383, lng: -84.2807 },
    '324': { lat: 30.1588, lng: -85.6602 }, '325': { lat: 30.4213, lng: -87.2169 },
    '326': { lat: 29.1872, lng: -82.1401 }, '327': { lat: 28.5383, lng: -81.3792 },
    '328': { lat: 28.5383, lng: -81.3792 }, '329': { lat: 28.5383, lng: -81.3792 },
    '330': { lat: 25.7617, lng: -80.1918 }, '331': { lat: 25.7617, lng: -80.1918 },
    '332': { lat: 25.7617, lng: -80.1918 }, '333': { lat: 26.1224, lng: -80.1373 },
    '334': { lat: 26.7056, lng: -80.0364 }, '335': { lat: 27.9506, lng: -82.4572 },
    '336': { lat: 27.9506, lng: -82.4572 }, '337': { lat: 27.9506, lng: -82.4572 },
    '338': { lat: 28.0222, lng: -82.5225 }, '339': { lat: 26.1420, lng: -81.7948 },
    '340': { lat: 26.6406, lng: -81.8723 }, '341': { lat: 26.6406, lng: -81.8723 },
    '342': { lat: 27.4989, lng: -82.5748 }, '344': { lat: 28.8028, lng: -81.2731 },
    '346': { lat: 27.9506, lng: -82.4572 }, '347': { lat: 28.5383, lng: -81.3792 },
    // GA
    '300': { lat: 33.7490, lng: -84.3880 }, '301': { lat: 33.7490, lng: -84.3880 },
    '302': { lat: 33.7490, lng: -84.3880 }, '303': { lat: 33.7490, lng: -84.3880 },
    '304': { lat: 33.5731, lng: -83.4673 }, '305': { lat: 33.7490, lng: -84.3880 },
    '306': { lat: 33.7490, lng: -84.3880 }, '307': { lat: 34.2576, lng: -84.8458 },
    '308': { lat: 32.8407, lng: -83.6324 }, '309': { lat: 32.0809, lng: -81.0912 },
    '310': { lat: 32.0809, lng: -81.0912 }, '311': { lat: 32.0809, lng: -81.0912 },
    '312': { lat: 32.0809, lng: -81.0912 }, '313': { lat: 32.0809, lng: -81.0912 },
    '314': { lat: 32.4610, lng: -84.9877 }, '315': { lat: 31.5785, lng: -84.1557 },
    '316': { lat: 31.2128, lng: -82.3541 }, '317': { lat: 30.8327, lng: -83.2785 },
    '318': { lat: 32.4610, lng: -84.9877 }, '319': { lat: 31.9686, lng: -81.0912 },
    // IL
    '600': { lat: 41.8781, lng: -87.6298 }, '601': { lat: 41.8781, lng: -87.6298 },
    '602': { lat: 42.0451, lng: -87.6877 }, '603': { lat: 41.8527, lng: -87.9520 },
    '604': { lat: 41.7508, lng: -88.1535 }, '605': { lat: 41.7508, lng: -88.1535 },
    '606': { lat: 41.8781, lng: -87.6298 }, '607': { lat: 41.8781, lng: -87.6298 },
    '608': { lat: 41.8781, lng: -87.6298 }, '609': { lat: 41.5067, lng: -90.5151 },
    '610': { lat: 41.5067, lng: -90.5151 }, '611': { lat: 41.5067, lng: -90.5151 },
    '612': { lat: 41.5067, lng: -90.5151 }, '613': { lat: 40.1106, lng: -88.2073 },
    '614': { lat: 40.1106, lng: -88.2073 }, '615': { lat: 40.6936, lng: -89.5890 },
    '616': { lat: 40.6936, lng: -89.5890 }, '617': { lat: 40.4757, lng: -88.9703 },
    '618': { lat: 40.4757, lng: -88.9703 }, '619': { lat: 40.4757, lng: -88.9703 },
    '620': { lat: 39.7817, lng: -89.6501 }, '622': { lat: 39.7817, lng: -89.6501 },
    '623': { lat: 38.6270, lng: -90.1994 }, '624': { lat: 38.5200, lng: -89.9839 },
    '625': { lat: 39.1978, lng: -89.6368 }, '626': { lat: 39.7817, lng: -89.6501 },
    '627': { lat: 39.7817, lng: -89.6501 }, '628': { lat: 38.6270, lng: -90.1994 },
    '629': { lat: 37.7270, lng: -89.2167 },
    // AR
    '716': { lat: 36.3729, lng: -94.2088 }, '717': { lat: 36.3729, lng: -94.2088 },
    '718': { lat: 36.0726, lng: -94.1574 }, '719': { lat: 36.0726, lng: -94.1574 },
    '720': { lat: 34.7465, lng: -92.2896 }, '721': { lat: 34.7465, lng: -92.2896 },
    '722': { lat: 34.7465, lng: -92.2896 }, '723': { lat: 35.3859, lng: -94.3985 },
    '724': { lat: 35.3859, lng: -94.3985 }, '725': { lat: 36.3729, lng: -94.2088 },
    '726': { lat: 35.8423, lng: -90.7043 }, '727': { lat: 36.3381, lng: -94.1185 },
    '728': { lat: 35.2010, lng: -91.8318 }, '729': { lat: 35.3859, lng: -94.3985 },
  };

  // Geographic coordinates mapping [lng, lat] for react-simple-maps
  const GEO_COORDS = {
    // West Coast
    'Los Angeles, CA': [-118.2437, 34.0522], 'LA': [-118.2437, 34.0522],
    'San Francisco, CA': [-122.4194, 37.7749], 'SF': [-122.4194, 37.7749],
    'San Diego, CA': [-117.1611, 32.7157], 'SD': [-117.1611, 32.7157],
    'Seattle, WA': [-122.3321, 47.6062], 'SEA': [-122.3321, 47.6062],
    'Portland, OR': [-122.6765, 45.5152], 'POR': [-122.6765, 45.5152],
    'Phoenix, AZ': [-112.0740, 33.4484], 'PHX': [-112.0740, 33.4484],
    'Las Vegas, NV': [-115.1398, 36.1699], 'LV': [-115.1398, 36.1699],
    'Denver, CO': [-104.9903, 39.7392], 'DEN': [-104.9903, 39.7392],
    'Salt Lake City, UT': [-111.8910, 40.7608], 'SLC': [-111.8910, 40.7608],
    // Central
    'Dallas, TX': [-96.7970, 32.7767], 'DAL': [-96.7970, 32.7767],
    'Houston, TX': [-95.3698, 29.7604], 'HOU': [-95.3698, 29.7604],
    'San Antonio, TX': [-98.4936, 29.4241], 'SAT': [-98.4936, 29.4241],
    'Austin, TX': [-97.7431, 30.2672], 'AUS': [-97.7431, 30.2672],
    'Kansas City, MO': [-94.5786, 39.0997], 'KC': [-94.5786, 39.0997],
    'Oklahoma City, OK': [-97.5164, 35.4676], 'OKC': [-97.5164, 35.4676],
    'Minneapolis, MN': [-93.2650, 44.9778], 'MSP': [-93.2650, 44.9778],
    'Chicago, IL': [-87.6298, 41.8781], 'CHI': [-87.6298, 41.8781],
    'St. Louis, MO': [-90.1994, 38.6270], 'STL': [-90.1994, 38.6270],
    'Memphis, TN': [-90.0490, 35.1495], 'MEM': [-90.0490, 35.1495],
    'New Orleans, LA': [-90.0715, 29.9511], 'MSY': [-90.0715, 29.9511],
    // East Coast
    'Atlanta, GA': [-84.3880, 33.7490], 'ATL': [-84.3880, 33.7490],
    'Miami, FL': [-80.1918, 25.7617], 'MIA': [-80.1918, 25.7617],
    'Jacksonville, FL': [-81.6557, 30.3322], 'JAX': [-81.6557, 30.3322],
    'Charlotte, NC': [-80.8431, 35.2271], 'CLT': [-80.8431, 35.2271],
    'Nashville, TN': [-86.7816, 36.1627], 'BNA': [-86.7816, 36.1627],
    'Detroit, MI': [-83.0458, 42.3314], 'DET': [-83.0458, 42.3314],
    'Indianapolis, IN': [-86.1581, 39.7684], 'IND': [-86.1581, 39.7684],
    'Cincinnati, OH': [-84.5120, 39.1031], 'CVG': [-84.5120, 39.1031],
    'Cleveland, OH': [-81.6944, 41.4993], 'CLE': [-81.6944, 41.4993],
    'Pittsburgh, PA': [-79.9959, 40.4406], 'PIT': [-79.9959, 40.4406],
    'Philadelphia, PA': [-75.1652, 39.9526], 'PHL': [-75.1652, 39.9526],
    'New York, NY': [-74.0060, 40.7128], 'NYC': [-74.0060, 40.7128], 'NY': [-74.0060, 40.7128],
    'Boston, MA': [-71.0589, 42.3601], 'BOS': [-71.0589, 42.3601],
    'Baltimore, MD': [-76.6122, 39.2904], 'BWI': [-76.6122, 39.2904],
    'Washington, DC': [-77.0369, 38.9072], 'DCA': [-77.0369, 38.9072],
    // Bentonville (Walmart HQ)
    'Bentonville, AR': [-94.2088, 36.3729], 'XNA': [-94.2088, 36.3729],
    // State fallbacks (center of state)
    'CA': [-119.4179, 36.7783], 'TX': [-99.9018, 31.9686], 'FL': [-81.5158, 27.6648],
    'IL': [-89.3985, 40.6331], 'GA': [-82.9001, 32.1656],
    'OH': [-82.9071, 40.4173], 'PA': [-77.1945, 41.2033], 'MI': [-85.6024, 44.3148],
    'NC': [-79.0193, 35.7596], 'TN': [-86.5804, 35.5175], 'MO': [-91.8318, 37.9643],
    'AR': [-91.8318, 35.2010], 'AZ': [-111.0937, 34.0489], 'CO': [-105.7821, 39.5501],
    'WA': [-120.7401, 47.7511], 'OR': [-120.5542, 43.8041], 'NV': [-116.4194, 38.8026],
  };

  // TransitTimeMap handles origins internally - we just pass shipPoints
  // No need for transitOrigins useMemo since component processes shipPoints

  // Build unique destinations from TMS data with geographic coordinates
  const transitDestinations = useMemo(() => {
    const destMap = new Map();

    routingData.orders.forEach(order => {
      const destId = safeValue(order.load_dest);
      const destType = safeValue(order.load_dest_type) || 'DC';
      const destCity = safeValue(order.store_city);
      const destState = safeValue(order.store_state);

      if (destId && !destMap.has(destId)) {
        // Try to find geographic coordinates
        let coords = null;
        if (destCity && destState) {
          coords = GEO_COORDS[`${destCity}, ${destState}`];
        }
        if (!coords && destState) {
          coords = GEO_COORDS[destState];
        }
        // Check if it's a known Walmart DC
        if (!coords && WALMART_DCS[destId]) {
          coords = WALMART_DCS[destId].coordinates;
        }
        // Default to center of US if no match
        if (!coords) {
          coords = [-98.5795, 39.8283]; // US center
        }

        destMap.set(destId, {
          id: destId,
          name: destType === 'DC' ? `DC ${destId}` : destType === 'CP' ? `CP ${destId}` : `Store ${destId}`,
          city: destCity && destState ? `${destCity}, ${destState}` : destState || 'Unknown',
          type: destType,
          coordinates: coords
        });
      }
    });

    return Array.from(destMap.values());
  }, [routingData.orders]);

  // Build routes from TMS data (Ship_Point -> Load_Dest with Mode)
  const transitRoutes = useMemo(() => {
    const routeMap = new Map();

    routingData.orders.forEach(order => {
      const origin = safeValue(order.ship_point);
      const destId = safeValue(order.load_dest);
      const mode = safeValue(order.mode) || 'TL';

      if (origin && destId) {
        const key = `${origin}-${destId}-${mode}`;
        if (!routeMap.has(key)) {
          routeMap.set(key, {
            origin,
            destId,
            mode,
            days: mode === 'Rail' ? 7 : mode === 'LTL' ? 5 : 3,
            count: 1
          });
        } else {
          routeMap.get(key).count++;
        }
      }
    });

    return Array.from(routeMap.values());
  }, [routingData.orders]);

  const getRoutesToDest = (destId) => transitRoutes.filter(r => r.destId === destId);
  const getDestById = (destId) => transitDestinations.find(d => d.id === destId);

  // For table display
  const sampleTransitRoutes = transitRoutes;

  const resetNoteForm = () => {
    setSearchedOrder(null);
    setNoteForm({
      lookup_type: 'po', lookup_value: '', mabd: '',
      original_appt_date: '', override_appt_date: '',
      reason: '', responsibility: '', note: '',
      ticket_number: '', ticket_sent: false
    });
  };

  // Open note modal for an order
  const openNoteModal = (order, existingNote = null) => {
    setNoteModalOrder(order);
    setNoteModalExisting(existingNote);
    setNoteModalOpen(true);
  };

  // Save note handler
  const handleSaveNote = async (noteData) => {
    try {
      if (noteModalExisting) {
        // Update existing note
        await RoutingNotesService.updateNote(noteModalExisting.id, noteData);
        console.log('Note updated successfully');
      } else {
        // Create new note
        await RoutingNotesService.createNote(noteData);
        console.log('Note created successfully');
      }
      // Notes will auto-update via subscription
    } catch (error) {
      console.error('Failed to save note:', error);
      throw error;
    }
  };

  // Delete note handler
  const handleDeleteNote = async (noteId) => {
    try {
      await RoutingNotesService.deleteNote(noteId);
      console.log('Note deleted successfully');
    } catch (error) {
      console.error('Failed to delete note:', error);
      throw error;
    }
  };

  // Save note from Notes tab form (legacy form handling)
  const handleSaveNoteFromTab = async () => {
    if (!searchedOrder) return;
    try {
      const noteData = {
        po_number: safeValue(searchedOrder.po_number) || '',
        load_id: safeValue(searchedOrder.load_id) || '',
        original_pickup_date: noteForm.original_appt_date || safeValue(searchedOrder.carrier_pu_date) || '',
        override_pickup_date: noteForm.override_appt_date || '',
        reason: noteForm.reason || '',
        responsibility: noteForm.responsibility || '',
        note_text: noteForm.note || '',
        ticket_number: noteForm.ticket_number || '',
        ticket_sent: noteForm.ticket_sent || false,
        mabd: noteForm.mabd || '',
        warehouse: safeValue(searchedOrder.warehouse) || safeValue(searchedOrder.ship_point) || '',
        destination: safeValue(searchedOrder.po_dest) || '',
        cases: safeValue(searchedOrder.cases) || 0,
      };

      // Check if note already exists for this PO
      const existingNote = routingNotes[safeValue(searchedOrder.po_number)];
      if (existingNote) {
        await RoutingNotesService.updateNote(existingNote.id, noteData);
      } else {
        await RoutingNotesService.createNote(noteData);
      }

      // Reset form
      resetNoteForm();
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const handleNotesSearch = () => {
    if (!notesSearch.value.trim()) return;
    const searchVal = notesSearch.value.trim();
    const found = routingData.orders.find(o =>
      (notesSearch.type === 'po' && safeValue(o.po_number) === searchVal) ||
      (notesSearch.type === 'load' && safeValue(o.load_id) === searchVal)
      // Note: pick_ticket search requires ERP data
    );
    if (found) {
      setSearchedOrder(found);
      setNoteForm(prev => ({ ...prev, lookup_type: notesSearch.type, lookup_value: searchVal, mabd: safeValue(found.mabd) || '' }));
    }
  };

  const clearFilters = () => {
    setFilterWarehouse(null);
    setSearchText('');
    setSelectedFilter(null);
    setPoFilterStatus(null);
    setPoFilterMode(null);
    setPoFilterAlert(null);
    setFilterDestState(null);
    setFilterMABD(null);
    setFilterShipDate(null);
    setFilterPO(null);
    setFilterPickWindow(null);
  };

  // ==================== SHIP POINTS HANDLERS ====================
  const openShipPointModal = (shipPoint = null) => {
    if (shipPoint) {
      setEditingShipPoint(shipPoint);
      setShipPointForm({
        code: shipPoint.code || '',
        location: shipPoint.location || '',
        name: shipPoint.name || '',
        address: shipPoint.address || '',
        city: shipPoint.city || '',
        state: shipPoint.state || '',
        zip: shipPoint.zip || '',
        active: shipPoint.active !== false
      });
    } else {
      setEditingShipPoint(null);
      setShipPointForm({
        code: '', location: '', name: '', address: '', city: '', state: '', zip: '', active: true
      });
    }
    setShipPointModalOpen(true);
  };

  const handleSaveShipPoint = async () => {
    try {
      if (editingShipPoint) {
        await ShipPointsService.updateShipPoint(editingShipPoint.id, shipPointForm);
      } else {
        await ShipPointsService.createShipPoint(shipPointForm);
      }
      setShipPointModalOpen(false);
      setEditingShipPoint(null);
    } catch (error) {
      console.error('Failed to save ship point:', error);
      alert('Failed to save: ' + error.message);
    }
  };

  const handleDeleteShipPoint = async (shipPointId) => {
    if (!window.confirm('Are you sure you want to delete this ship point?')) return;
    try {
      await ShipPointsService.deleteShipPoint(shipPointId);
    } catch (error) {
      console.error('Failed to delete ship point:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleToggleShipPointActive = async (shipPoint) => {
    try {
      await ShipPointsService.updateShipPoint(shipPoint.id, { active: !shipPoint.active });
    } catch (error) {
      console.error('Failed to toggle ship point:', error);
    }
  };

  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(shipPoints.map(sp => sp.location).filter(Boolean))];
    return locations.sort();
  }, [shipPoints]);

  // ==================== USER HANDLERS ====================
  const openUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        // CHA-97 / PR-C: shape from listUsers callable — displayName + roleId
        email: user.email || '',
        name: user.displayName || '',
        role: user.roleId || 'viewer',
        tempPassword: ''
      });
    } else {
      setEditingUser(null);
      setUserForm({
        email: '', name: '', role: 'viewer', tempPassword: ''
      });
    }
    setUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // CHA-97 / PR-C: TenantUsersService.updateUser routes through
        // assignUserRole callable. Only `role` persists; `name` is
        // intentionally not passed (would be dropped server-side anyway
        // and the input is disabled per CHA-117 follow-up).
        // CodeRabbit C-3 / Codex P2-2: callable rows are keyed by `uid`,
        // not `id`. Fall back to `id` only for any legacy callsite.
        await TenantUsersService.updateUser(editingUser.uid ?? editingUser.id, {
          role: userForm.role
        });
      } else {
        if (!userForm.email || !userForm.tempPassword) {
          alert('Email and temporary password are required');
          return;
        }
        await TenantUsersService.createUser({
          email: userForm.email,
          name: userForm.name,
          role: userForm.role
        }, userForm.tempPassword);
      }
      // Refresh the user list — without the snapshot subscription, the
      // table goes stale unless we re-fetch after a mutation.
      await refreshUsers();
      setUserModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to save user:', error);
      alert('Failed to save: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    try {
      await TenantUsersService.deleteUser(userId);
      await refreshUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await TenantUsersService.setUserDisabled(user.uid, !user.disabled);
      await refreshUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const handleSendPasswordReset = async (email) => {
    try {
      await TenantUsersService.sendPasswordReset(email);
      alert('Password reset email sent to ' + email);
    } catch (error) {
      console.error('Failed to send password reset:', error);
      alert('Failed to send reset email: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-800">Routing</h1>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">BETA</span>
          </div>
          <p className="text-slate-500 mt-1">Walmart & Sam's Club routing management</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'dashboard' && (
            <div className="flex items-center gap-3">
              {routingData.uploadDate && (
                <span className="text-xs text-slate-500">
                  Data from: {safeValue(routingData.uploadDate)}
                </span>
              )}
              <button
                onClick={fetchRoutingData}
                disabled={routingData.isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                <RefreshCw size={16} className={routingData.isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          )}
          {/* User info and sign out for non-platform owners (platform owners have it in top nav) */}
          {userProfile?.role !== 'platform_owner' && onSignOut && (
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-slate-200">
              <span className="text-sm text-slate-600">{userProfile?.email}</span>
              <button
                onClick={onSignOut}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Truck size={16} />
              Dashboard
            </div>
          </button>
          {canUploadData && (
            <button
              onClick={() => setActiveTab('dataflow')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'dataflow'
                  ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Package size={16} />
                Data Workflow
              </div>
            </button>
          )}
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'notes'
                ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare size={16} />
              Notes
              <Lock size={12} className="text-slate-400" />
            </div>
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'documents'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText size={16} />
              Documents
              {documents.filter(d => d.status === 'unmatched').length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                  {documents.filter(d => d.status === 'unmatched').length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('transit')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'transit'
                ? 'text-cyan-600 border-b-2 border-cyan-600 bg-cyan-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Clock size={16} />
              Transit Time
            </div>
          </button>
          {canViewSettings && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'settings'
                  ? 'text-slate-700 border-b-2 border-slate-700 bg-slate-50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Settings size={16} />
                Settings
              </div>
            </button>
          )}
          {isPlatformOwner && (
            <button
              onClick={() => setActiveTab('plan')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'plan'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <BookOpen size={16} />
                Feature Plan
              </div>
            </button>
          )}
        </div>
      </div>

      {/* ==================== DASHBOARD TAB ==================== */}
      {activeTab === 'dashboard' && (
        <>
          {/* Loading/Error State */}
          {routingData.isLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
              <RefreshCw size={16} className="text-blue-600 animate-spin" />
              <span className="text-sm text-blue-700">Loading routing data from BigQuery...</span>
            </div>
          )}
          {routingData.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-600" />
              <span className="text-sm text-red-700">{safeValue(routingData.error)}</span>
              <button
                onClick={fetchRoutingData}
                className="ml-auto text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                Retry
              </button>
            </div>
          )}

          {/* Active Filters Bar */}
          {hasActiveFilters && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-blue-700 mr-1">Active Filters:</span>

              {filterWarehouse && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700">
                  <span className="text-blue-500">Ship Point:</span> {filterWarehouse}
                  <button onClick={() => setFilterWarehouse(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {searchText && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700">
                  <span className="text-blue-500">Search:</span> "{searchText}"
                  <button onClick={() => setSearchText('')} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-700">
                  <span className="text-blue-500">Status:</span> {selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}
                  <button onClick={() => setSelectedFilter(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filterMABD && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-amber-200 rounded-full text-xs text-amber-700">
                  <span className="text-amber-500">MABD:</span> {filterMABD}
                  <button onClick={() => setFilterMABD(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filterShipDate && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-green-200 rounded-full text-xs text-green-700">
                  <span className="text-green-500">{filterShipDate === 'Pending' || filterShipDate === 'Confirmed' ? 'Status:' : 'Ship Date:'}</span> {filterShipDate}
                  <button onClick={() => setFilterShipDate(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filterDestState && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-purple-200 rounded-full text-xs text-purple-700">
                  <span className="text-purple-500">State:</span> {STATE_COORDS[filterDestState]?.name || filterDestState}
                  <button onClick={() => setFilterDestState(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filterPO && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-700">
                  <span className="text-slate-500">PO:</span> {filterPO}
                  <button onClick={() => setFilterPO(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {poFilterStatus && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-700">
                  <span className="text-slate-500">Table Status:</span> {poFilterStatus}
                  <button onClick={() => setPoFilterStatus(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {poFilterMode && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-700">
                  <span className="text-slate-500">Mode:</span> {poFilterMode}
                  <button onClick={() => setPoFilterMode(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {poFilterAlert && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-red-200 rounded-full text-xs text-red-700">
                  <span className="text-red-500">Alert:</span> {poFilterAlert}
                  <button onClick={() => setPoFilterAlert(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filterPickWindow && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-orange-200 rounded-full text-xs text-orange-700">
                  <span className="text-orange-500">Pick Window:</span> {filterPickWindow}
                  <button onClick={() => setFilterPickWindow(null)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              )}

              {/* Clear All button */}
              <button
                onClick={clearFilters}
                className="ml-auto flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium hover:bg-red-200 transition-colors"
              >
                <X size={12} />
                Clear All
              </button>
            </div>
          )}

          {/* A1: Filters & Summary Cards */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {/* Filter Mode Toggle: Ship Point vs Location */}
              <div className="flex items-center gap-1 border-r border-slate-200 pr-3">
                <button
                  onClick={() => setFilterMode('shipPoint')}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    filterMode === 'shipPoint' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  Ship Point
                </button>
                <button
                  onClick={() => setFilterMode('location')}
                  className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                    filterMode === 'location' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  Location
                </button>
              </div>

              {/* Warehouse/Location Filter - Dynamic */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setFilterWarehouse(null)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    !filterWarehouse ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All {filterMode === 'location' ? 'Locations' : 'Ship Points'}
                </button>
                {filterMode === 'location' ? (
                  // Location mode - group by location
                  (uniqueLocations.length > 0 ? uniqueLocations : ['Los Angeles', 'Dallas', 'Chicago']).map(loc => (
                    <button
                      key={loc}
                      onClick={() => setFilterWarehouse(loc === filterWarehouse ? null : loc)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        filterWarehouse === loc ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {loc}
                    </button>
                  ))
                ) : (
                  // Ship Point mode - individual ship points
                  (shipPoints.length > 0
                    ? shipPoints.filter(sp => sp.active).map(sp => sp.code)
                    : routingData.warehouses.length > 0
                      ? routingData.warehouses.map(w => safeValue(w.warehouse)).filter(Boolean)
                      : ['IMG', 'LAP', 'CLS', 'BL']
                  ).map(wh => (
                    <button
                      key={wh}
                      onClick={() => setFilterWarehouse(wh === filterWarehouse ? null : wh)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        filterWarehouse === wh ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {wh}
                    </button>
                  ))
                )}
              </div>

              {/* Search */}
              <div className="flex items-center gap-2">
                <Search size={14} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Search PO, PT, Load..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="px-3 py-1.5 border border-slate-200 rounded-md text-xs w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Display Mode Toggle: Cases vs Dollars */}
              {/* Dollars only enabled when ERP data with amounts is available */}
              {(() => {
                const hasDollarData = (summaryMetrics.totalDollars || 0) > 0;
                return (
                  <div className="flex items-center gap-1 ml-auto bg-slate-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setDisplayMode('cases')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        displayMode === 'cases' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Package size={12} className="inline mr-1" />
                      Cases
                    </button>
                    <button
                      onClick={() => hasDollarData && setDisplayMode('dollars')}
                      disabled={!hasDollarData}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        !hasDollarData
                          ? 'text-slate-300 cursor-not-allowed'
                          : displayMode === 'dollars'
                            ? 'bg-green-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title={!hasDollarData ? 'Requires ERP/Order data with amounts' : 'Show dollar values'}
                    >
                      <DollarSign size={12} className="inline mr-1" />
                      Dollars
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Summary Cards - From BigQuery API */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard
                title="Scheduled"
                description="Has carrier pickup date assigned"
                value={displayMode === 'cases'
                  ? `${summaryMetrics.scheduled.total.toLocaleString()} cases`
                  : `$${summaryMetrics.scheduled.dollars.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                count={summaryMetrics.scheduled.count}
                color="green"
                onClick={() => setSelectedFilter(selectedFilter === 'scheduled' ? null : 'scheduled')}
                active={selectedFilter === 'scheduled'}
              />
              <SummaryCard
                title="Confirmed"
                description="On a load, waiting for pickup date"
                value={displayMode === 'cases'
                  ? `${summaryMetrics.confirmed.total.toLocaleString()} cases`
                  : `$${summaryMetrics.confirmed.dollars.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                count={summaryMetrics.confirmed.count}
                color="blue"
                onClick={() => setSelectedFilter(selectedFilter === 'confirmed' ? null : 'confirmed')}
                active={selectedFilter === 'confirmed'}
              />
              <SummaryCard
                title="Pending"
                description="Not yet assigned to a load"
                value={displayMode === 'cases'
                  ? `${summaryMetrics.pending.total.toLocaleString()} cases`
                  : `$${summaryMetrics.pending.dollars.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                count={summaryMetrics.pending.count}
                color="amber"
                onClick={() => setSelectedFilter(selectedFilter === 'pending' ? null : 'pending')}
                active={selectedFilter === 'pending'}
              />
              <SummaryCard
                title="Total"
                value={displayMode === 'cases'
                  ? `${(safeValue(routingData.summary?.totalCases) || 0).toLocaleString()} cases`
                  : `$${(summaryMetrics.totalDollars || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                subtitle={`${safeValue(routingData.summary?.uniquePOs) || routingData.orders.length} POs`}
                color="slate"
              />
            </div>
          </div>

          {/* Row B: B1 Appt vs MABD Matrix | B2 Pending by Pick Window */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* B1: Appointment vs MABD Matrix - Cases mode (no ERP $ data) */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                  Ship Date vs MABD
                  <span className="relative group">
                    <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[10px] cursor-help">i</span>
                    <span className="absolute left-5 top-0 z-20 hidden group-hover:block w-56 p-2 text-xs font-normal text-slate-600 bg-white border border-slate-200 rounded-lg shadow-lg">
                      Scheduled orders by Carrier Pickup Date (columns) vs Must Arrive By Date (rows). Confirmed column shows orders with a load but no pickup date yet.
                    </span>
                  </span>
                </h3>
                <div className="flex items-center gap-1 text-[10px]">
                  <span className={`px-2 py-1 rounded ${displayMode === 'dollars' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'}`}>
                    {displayMode === 'dollars' ? 'Dollars' : 'Cases'}
                  </span>
                </div>
              </div>
              <div className="overflow-auto max-h-[220px]">
                {apptMatrix.rows.length > 0 ? (
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b border-slate-200">MABD</th>
                        {apptMatrix.columns.map(col => {
                          const style = getMatrixColumnStyle(col);
                          const isSelected = filterShipDate === col;
                          return (
                            <th
                              key={col}
                              className={`px-2 py-1.5 text-right font-semibold border-b border-l border-slate-200 whitespace-nowrap cursor-pointer hover:bg-blue-100 ${isSelected ? 'bg-blue-200' : style.header || 'text-slate-600'}`}
                              onClick={() => {
                                // Toggle ship date filter (clears MABD filter when clicking header)
                                setFilterMABD(null);
                                setFilterShipDate(filterShipDate === col ? null : col);
                              }}
                            >
                              {col}
                            </th>
                          );
                        })}
                        <th className="px-2 py-1.5 text-right font-semibold text-slate-700 bg-slate-100 border-b border-l border-slate-200">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apptMatrix.rows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-blue-50/50">
                          <td
                            className={`px-2 py-1.5 font-medium border-b border-slate-100 cursor-pointer hover:bg-blue-100 ${filterMABD === row.mabd ? 'bg-blue-200 text-blue-800' : 'text-slate-700'}`}
                            onClick={() => setFilterMABD(filterMABD === row.mabd ? null : row.mabd)}
                          >
                            {row.mabd}
                          </td>
                          {apptMatrix.columns.map(col => {
                            const style = getMatrixColumnStyle(col);
                            const isSelected = filterMABD === row.mabd && filterShipDate === col;
                            return (
                              <td
                                key={col}
                                className={`px-2 py-1.5 text-right border-b border-l border-slate-100 cursor-pointer hover:bg-blue-100 ${isSelected ? 'bg-blue-300 ring-2 ring-blue-500' : style.cell} ${row[col] ? 'text-slate-700' : 'text-slate-300'}`}
                                onClick={() => {
                                  if (filterMABD === row.mabd && filterShipDate === col) {
                                    // Deselect - clear both filters
                                    setFilterMABD(null);
                                    setFilterShipDate(null);
                                  } else {
                                    // Select - set both filters (col can be 'Confirmed' or a date)
                                    setFilterMABD(row.mabd);
                                    setFilterShipDate(col);
                                  }
                                }}
                              >
                                {row[col]
                                  ? (displayMode === 'dollars'
                                    ? `$${Math.round(row[col]).toLocaleString()}`
                                    : row[col].toLocaleString())
                                  : '—'}
                              </td>
                            );
                          })}
                          <td
                            className={`px-2 py-1.5 text-right font-semibold bg-slate-50 border-b border-l border-slate-200 cursor-pointer hover:bg-blue-100 ${filterMABD === row.mabd && !filterShipDate ? 'bg-blue-200 text-blue-800' : 'text-slate-800'}`}
                            onClick={() => {
                              setFilterShipDate(null);
                              setFilterMABD(filterMABD === row.mabd ? null : row.mabd);
                            }}
                          >
                            {displayMode === 'dollars'
                              ? `$${Math.round(row.total).toLocaleString()}`
                              : row.total.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="sticky bottom-0 bg-slate-100">
                      <tr>
                        <td className="px-2 py-1.5 font-bold text-slate-800 border-t border-slate-200">Total</td>
                        {apptMatrix.columns.map(col => {
                          const total = apptMatrix.rows.reduce((sum, r) => sum + (r[col] || 0), 0);
                          const style = getMatrixColumnStyle(col);
                          const isSelected = filterShipDate === col && !filterMABD;
                          return (
                            <td
                              key={col}
                              className={`px-2 py-1.5 text-right font-bold border-t border-l border-slate-200 cursor-pointer hover:bg-blue-100 ${isSelected ? 'bg-blue-200 text-blue-800' : `text-slate-800 ${style.footer}`}`}
                              onClick={() => {
                                setFilterMABD(null);
                                setFilterShipDate(filterShipDate === col ? null : col);
                              }}
                            >
                              {displayMode === 'dollars' ? `$${Math.round(total).toLocaleString()}` : total.toLocaleString()}
                            </td>
                          );
                        })}
                        <td
                          className={`px-2 py-1.5 text-right font-bold border-t border-l border-slate-200 cursor-pointer hover:bg-blue-100 ${!filterMABD && !filterShipDate ? 'text-blue-600' : 'text-slate-600'}`}
                          onClick={() => {
                            // Clear matrix filters when clicking grand total
                            setFilterMABD(null);
                            setFilterShipDate(null);
                          }}
                          title="Click to clear matrix filters"
                        >
                          {displayMode === 'dollars'
                            ? `$${Math.round(apptMatrix.rows.reduce((sum, r) => sum + r.total, 0)).toLocaleString()}`
                            : apptMatrix.rows.reduce((sum, r) => sum + r.total, 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="p-4 text-center text-slate-400 text-sm">No scheduled orders with ship dates</div>
                )}
              </div>
            </div>

            {/* B2: Confirmed Matrix - Pick Window vs MABD */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                  Pick Window vs MABD (Confirmed)
                  <span className="relative group">
                    <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-500 text-[10px] cursor-help">i</span>
                    <span className="absolute left-5 top-0 z-20 hidden group-hover:block w-56 p-2 text-xs font-normal text-slate-600 bg-white border border-slate-200 rounded-lg shadow-lg">
                      Confirmed orders (on load, no pickup date yet) by Pick Window Start (columns) vs MABD (rows).
                    </span>
                  </span>
                </h3>
                <div className="flex items-center gap-1 text-[10px]">
                  <span className={`px-2 py-1 rounded ${displayMode === 'dollars' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'}`}>
                    {displayMode === 'dollars' ? 'Dollars' : 'Cases'}
                  </span>
                </div>
              </div>

              {/* Fixed height container */}
              <div className="h-[220px] overflow-auto">
                {confirmedMatrix.rows.length > 0 ? (
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-slate-50 z-10">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b border-slate-200">MABD</th>
                          {confirmedMatrix.columns.map(col => {
                            const style = getMatrixColumnStyle(col);
                            return (
                              <th
                                key={col}
                                className={`px-2 py-1.5 text-right font-semibold border-b border-l border-slate-200 whitespace-nowrap cursor-pointer hover:bg-amber-100 ${style.header || 'text-slate-600'}`}
                              >
                                {col}
                              </th>
                            );
                          })}
                          <th className="px-2 py-1.5 text-right font-semibold text-slate-700 bg-slate-100 border-b border-l border-slate-200">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {confirmedMatrix.rows.map((row, idx) => (
                          <tr key={idx} className="hover:bg-amber-50/50">
                            <td
                              className={`px-2 py-1.5 font-medium border-b border-slate-100 cursor-pointer hover:bg-amber-100 ${filterMABD === row.mabd && filterShipDate === 'Confirmed' ? 'bg-amber-200 text-amber-800' : 'text-slate-700'}`}
                              onClick={() => {
                                if (filterMABD === row.mabd && filterShipDate === 'Confirmed') {
                                  setFilterMABD(null);
                                  setFilterShipDate(null);
                                } else {
                                  setFilterMABD(row.mabd);
                                  setFilterShipDate('Confirmed');
                                }
                              }}
                            >
                              {row.mabd}
                            </td>
                            {confirmedMatrix.columns.map(col => {
                              const style = getMatrixColumnStyle(col);
                              const isSelected = filterMABD === row.mabd && filterShipDate === 'Confirmed';
                              return (
                                <td
                                  key={col}
                                  className={`px-2 py-1.5 text-right border-b border-l border-slate-100 cursor-pointer hover:bg-amber-100 ${isSelected ? 'bg-amber-100' : style.cell} ${row[col] ? 'text-slate-700' : 'text-slate-300'}`}
                                  onClick={() => {
                                    if (filterMABD === row.mabd && filterShipDate === 'Confirmed') {
                                      setFilterMABD(null);
                                      setFilterShipDate(null);
                                    } else {
                                      setFilterMABD(row.mabd);
                                      setFilterShipDate('Confirmed');
                                    }
                                  }}
                                >
                                  {row[col]
                                    ? (displayMode === 'dollars'
                                      ? `$${Math.round(row[col]).toLocaleString()}`
                                      : row[col].toLocaleString())
                                    : '—'}
                                </td>
                              );
                            })}
                            <td
                              className={`px-2 py-1.5 text-right font-semibold bg-slate-50 border-b border-l border-slate-200 cursor-pointer hover:bg-amber-100 ${filterMABD === row.mabd && filterShipDate === 'Confirmed' ? 'bg-amber-200 text-amber-800' : 'text-slate-800'}`}
                              onClick={() => {
                                if (filterMABD === row.mabd && filterShipDate === 'Confirmed') {
                                  setFilterMABD(null);
                                  setFilterShipDate(null);
                                } else {
                                  setFilterMABD(row.mabd);
                                  setFilterShipDate('Confirmed');
                                }
                              }}
                            >
                              {displayMode === 'dollars'
                                ? `$${Math.round(row.total).toLocaleString()}`
                                : row.total.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100 sticky bottom-0">
                        <tr>
                          <td className="px-2 py-1.5 font-bold text-slate-800 border-t border-slate-200">Total</td>
                          {confirmedMatrix.columns.map(col => {
                            const total = confirmedMatrix.rows.reduce((sum, r) => sum + (r[col] || 0), 0);
                            const style = getMatrixColumnStyle(col);
                            return <td key={col} className={`px-2 py-1.5 text-right font-bold text-slate-800 border-t border-l border-slate-200 ${style.footer}`}>
                              {displayMode === 'dollars' ? `$${Math.round(total).toLocaleString()}` : total.toLocaleString()}
                            </td>;
                          })}
                          <td className="px-2 py-1.5 text-right font-bold text-blue-600 border-t border-l border-slate-200">
                            {displayMode === 'dollars'
                              ? `$${Math.round(confirmedMatrix.rows.reduce((sum, r) => sum + r.total, 0)).toLocaleString()}`
                              : confirmedMatrix.rows.reduce((sum, r) => sum + r.total, 0).toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                ) : (
                  <div className="p-4 text-center text-slate-400 text-sm">No confirmed orders</div>
                )}
              </div>
            </div>
          </div>

          {/* Row C: PO Information Table (full width) */}
          <div>
            {/* C1: PO Information Table */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h3 className="text-sm font-semibold text-slate-700">PO Information</h3>
                    {/* Filter dropdowns */}
                    <div className="flex items-center gap-2">
                      {/* Status Filter */}
                      <div className="relative">
                        <select
                          value={poFilterStatus || ''}
                          onChange={(e) => setPoFilterStatus(e.target.value || null)}
                          className={`text-[10px] px-2 py-1 rounded border ${poFilterStatus ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600'} cursor-pointer`}
                        >
                          <option value="">Status</option>
                          {poFilterOptions.statuses.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      {/* Mode Filter */}
                      <div className="relative">
                        <select
                          value={poFilterMode || ''}
                          onChange={(e) => setPoFilterMode(e.target.value || null)}
                          className={`text-[10px] px-2 py-1 rounded border ${poFilterMode ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600'} cursor-pointer`}
                        >
                          <option value="">Mode</option>
                          {poFilterOptions.modes.map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                      {/* Alert Filter */}
                      <div className="relative">
                        <select
                          value={poFilterAlert || ''}
                          onChange={(e) => setPoFilterAlert(e.target.value || null)}
                          className={`text-[10px] px-2 py-1 rounded border ${poFilterAlert ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600'} cursor-pointer`}
                        >
                          <option value="">Alert</option>
                          {poFilterOptions.alerts.map(a => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      </div>
                      {/* Clear filters */}
                      {hasPoFilters && (
                        <button
                          onClick={clearPoFilters}
                          className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                        >
                          <X size={10} /> Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{filteredOrders.length} orders{hasPoFilters ? ' (filtered)' : ''}</span>
                    <button
                      onClick={handleExportPO}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] bg-green-50 text-green-700 rounded hover:bg-green-100"
                      title="Export to Excel"
                    >
                      <Download size={12} /> Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto overflow-y-auto max-h-[220px]" style={{ overflowX: 'auto' }}>
                <table className="text-xs" style={{ minWidth: '1720px' }}>
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">PO</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Notes</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Split</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">LoadID</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">WH</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Dest</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Carrier</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">PO Type</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Dept</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Event</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Mode</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Load</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Stage</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Route</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Ship</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Transit</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">ETA</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">MABD</th>
                      <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Alert</th>
                      <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Cases</th>
                      <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Pallets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, idx) => {
                      const status = safeValue(order.routing_status);
                      const statusFlag = safeValue(order.status_flag);
                      const mode = safeValue(order.mode);
                      // Mode icons
                      const modeIcon = mode === 'FLEET' ? '🚛' : mode === 'RAIL' ? '🚂' : mode === 'TL' ? '📦' : mode === 'LTL' ? '📫' : mode === 'SMPKG' ? '✉️' : '';
                      const modeText = mode && mode !== 'nan' && mode !== 'NULL' ? mode : '';
                      const poNum = safeValue(order.po_number);
                      const isSelected = filterPO === poNum;
                      return (
                        <tr
                          key={idx}
                          className={`border-b border-slate-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-100 ring-1 ring-blue-400' : statusFlag === 'Late Risk' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-blue-50/30'}`}
                          onClick={() => setFilterPO(isSelected ? null : poNum)}
                        >
                          <td className={`px-2 py-2 font-medium whitespace-nowrap ${isSelected ? 'text-blue-800' : 'text-blue-600'}`}>{poNum || '—'}</td>
                          <td className="px-2 py-2 text-center">
                            {routingNotes[poNum] ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openNoteModal(order, routingNotes[poNum]);
                                }}
                                className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                title={`Note: ${routingNotes[poNum].note_text || 'Click to edit'}`}
                              >
                                <FileText size={12} />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openNoteModal(order, null);
                                }}
                                className="inline-flex items-center justify-center w-6 h-6 text-slate-400 hover:bg-slate-100 rounded hover:text-slate-600 transition-colors"
                                title="Add note"
                              >
                                <span className="text-lg leading-none">+</span>
                              </button>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {splitPOLookup[poNum] ? (
                              <span
                                className="inline-flex items-center justify-center w-5 h-5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full cursor-pointer hover:bg-amber-200"
                                title={`${splitPOLookup[poNum]} loads - click to filter`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterPO(filterPO === poNum ? null : poNum);
                                }}
                              >
                                {splitPOLookup[poNum]}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{safeValue(order.load_id) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600">{safeValue(order.warehouse) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600">{safeValue(order.po_dest) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap truncate max-w-[100px]" title={safeValue(order.carrier_name)}>{safeValue(order.carrier_name) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{safeValue(order.po_type) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600">{safeValue(order.department) || '—'}</td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{safeValue(order.event_code) || '—'}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            {modeIcon && <span className="mr-1">{modeIcon}</span>}
                            <span className="text-slate-600">{modeText || '—'}</span>
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{safeValue(order.loading_method) || '—'}</td>
                          <td className="px-2 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              status === 'Confirmed' ? 'bg-blue-100 text-blue-700' :
                              status === 'Scheduled' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {status || '—'}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{formatShortDate(safeValue(order.confirmed_date)) || '—'}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            {routingNotes[poNum]?.override_pickup_date ? (
                              <span className="text-amber-600 font-medium" title={`Override: ${routingNotes[poNum].override_pickup_date} (Original: ${formatShortDate(safeValue(order.carrier_pu_date)) || '—'})`}>
                                {routingNotes[poNum].override_pickup_date}
                              </span>
                            ) : (
                              <span className="text-slate-700">{formatShortDate(safeValue(order.carrier_pu_date)) || '—'}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center text-slate-600">{safeValue(order.transit_days) != null ? safeValue(order.transit_days) + 'd' : '—'}</td>
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{formatShortDate(safeValue(order.expected_arrival)) || '—'}</td>
                          <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{formatShortDate(safeValue(order.mabd)) || '—'}</td>
                          <td className="px-2 py-2 text-center">
                            {statusFlag && statusFlag !== 'OK' ? (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                                statusFlag === 'Late Risk' ? 'bg-red-100 text-red-700' :
                                statusFlag === 'Missing Mode' ? 'bg-yellow-100 text-yellow-700' :
                                statusFlag === 'Missing Transit' ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {statusFlag}
                              </span>
                            ) : (
                              <span className="text-green-500">✓</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right text-slate-600">{safeValue(order.cases) || '—'}</td>
                          <td className="px-2 py-2 text-right text-slate-600">{safeValue(order.pallets) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredOrders.length === 0 && (
                  <div className="p-8 text-center text-slate-400">No orders match filters</div>
                )}
              </div>
            </div>
          </div>

{/* Row D: ERP Data Only - Hidden until ERP data is connected */}
          {/* TODO: Uncomment when ERP data is available
          <div className="relative">
            ... ERP section hidden ...
          </div>
          */}

          {/* Row E: E1 USA Map (2/3) | E2 Split Shipments (1/3) - matches PO Information height */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* E1: USA Map */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 h-[270px] flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-blue-600" />
                  <h3 className="text-sm font-semibold text-slate-700">Ship To Destinations</h3>
                  {filterDestState && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                      {STATE_COORDS[filterDestState]?.name || filterDestState}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {/* Ship Point Legend */}
                  <div className="flex items-center gap-2 text-[10px]">
                    {Object.entries(SHIP_POINT_COLORS).filter(([k]) => k !== 'default').map(([code, colors]) => (
                      <span key={code} className="flex items-center gap-1">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: colors.bg }}
                        />
                        <span className="text-slate-600">{code}</span>
                      </span>
                    ))}
                  </div>
                  {filterDestState && (
                    <button
                      onClick={() => setFilterDestState(null)}
                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                    >
                      <X size={10} /> Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center overflow-hidden p-2">
                <USAMap
                  data={mapFilteredOrders}
                  onStateClick={setFilterDestState}
                  selectedState={filterDestState}
                />
              </div>
            </div>

            {/* E2: Split Shipments - POs with multiple loads */}
            <div className="bg-white rounded-xl border border-slate-200 h-[270px] flex flex-col">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">Split Shipments</h3>
                <span className="text-xs text-slate-500">{splitShipments.length} POs</span>
              </div>
              <div className="overflow-auto flex-1">
                {splitShipments.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">PO</th>
                        <th className="px-2 py-2 text-center font-semibold text-slate-600 border-b border-slate-200">Loads</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">First</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">Last</th>
                        <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200">Cases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {splitShipments.map((split, idx) => {
                        const isSelected = filterPO === split.po;
                        return (
                          <tr
                            key={idx}
                            className={`cursor-pointer border-b border-slate-100 transition-colors ${isSelected ? 'bg-blue-100 ring-1 ring-blue-400' : 'hover:bg-blue-50/30'}`}
                            onClick={() => setFilterPO(isSelected ? null : split.po)}
                          >
                            <td className={`px-2 py-2 font-medium ${isSelected ? 'text-blue-800' : 'text-blue-600'}`}>{split.po}</td>
                            <td className="px-2 py-2 text-center text-slate-600">{split.loadCount}</td>
                            <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{split.firstShip}</td>
                            <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{split.lastShip}</td>
                            <td className="px-2 py-2 text-right text-slate-700">{split.cases.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-100">
                      <tr>
                        <td className="px-2 py-2 font-bold text-slate-800 border-t border-slate-200">Total</td>
                        <td className="px-2 py-2 text-center font-bold text-slate-800 border-t border-slate-200">
                          {splitShipments.reduce((sum, s) => sum + s.loadCount, 0)}
                        </td>
                        <td className="px-2 py-2 border-t border-slate-200"></td>
                        <td className="px-2 py-2 border-t border-slate-200"></td>
                        <td className="px-2 py-2 text-right font-bold text-blue-600 border-t border-slate-200">
                          {splitShipments.reduce((sum, s) => sum + s.cases, 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="p-4 text-center text-slate-400 text-sm">No split shipments found</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ==================== DATA WORKFLOW TAB ==================== */}
      {activeTab === 'dataflow' && (
        <div className="space-y-4">
          {/* Main Data Workflow Content */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
          {/* Pipeline Steps */}
          <div className="bg-white border-b border-slate-200 px-6 py-3">
            <div className="flex items-center justify-center gap-4">
              {/* Available: ERP */}
              <div className="flex items-center gap-2 px-3 py-1.5 border-2 border-dashed border-slate-300 rounded-lg hover:border-green-400 hover:bg-green-50 cursor-pointer transition-colors group">
                <div className="w-6 h-6 rounded bg-slate-100 group-hover:bg-green-100 flex items-center justify-center">
                  <FileText size={12} className="text-slate-400 group-hover:text-green-500" />
                </div>
                <span className="text-xs text-slate-500 group-hover:text-green-600">ERP</span>
              </div>

              {/* Available: 3PL */}
              <div className="flex items-center gap-2 px-3 py-1.5 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors group">
                <div className="w-6 h-6 rounded bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center">
                  <Package size={12} className="text-slate-400 group-hover:text-blue-500" />
                </div>
                <span className="text-xs text-slate-500 group-hover:text-blue-600">3PL</span>
              </div>

              <ArrowRight size={16} className="text-slate-300" />

              {/* Active: Walmart Routing Data */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200">
                <div className="w-6 h-6 rounded bg-purple-500 flex items-center justify-center">
                  <Truck size={12} className="text-white" />
                </div>
                <span className="text-xs font-medium text-purple-700">Walmart TMS</span>
                <CheckCircle2 size={14} className="text-green-500" />
              </div>

              <ArrowRight size={16} className="text-slate-300" />

              {/* Active: Transform */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-6 h-6 rounded bg-blue-500 flex items-center justify-center">
                  <RefreshCw size={12} className="text-white" />
                </div>
                <span className="text-xs font-medium text-blue-700">Transform</span>
              </div>
            </div>
          </div>

          {/* Data Source Sections */}
          <div className="p-6 space-y-6">

            {/* AVAILABLE DATA SOURCES - Click to configure */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* ERP DATA - Available */}
              <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-green-400 hover:bg-green-50/50 cursor-pointer transition-all group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-green-100 flex items-center justify-center">
                      <FileText size={16} className="text-slate-400 group-hover:text-green-500" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-600 group-hover:text-green-700">ERP Data</span>
                      <p className="text-[10px] text-slate-400">Your system's orders</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 group-hover:text-green-500">Setup required</span>
                </div>
              </div>

              {/* SUPPLIER ONE - Available */}
              <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-yellow-400 hover:bg-yellow-50/50 cursor-pointer transition-all group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-yellow-100 flex items-center justify-center">
                      <span className="text-slate-400 group-hover:text-yellow-500 text-lg">✳</span>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-600 group-hover:text-yellow-700">Supplier One</span>
                      <p className="text-[10px] text-slate-400">Walmart's view of orders</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 group-hover:text-yellow-500">Setup required</span>
                </div>
              </div>

              {/* 3PL - Available */}
              <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition-all group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center">
                      <Package size={16} className="text-slate-400 group-hover:text-blue-500" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-600 group-hover:text-blue-700">3PL</span>
                      <p className="text-[10px] text-slate-400">Ship logs & pick status</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 group-hover:text-blue-500">Setup required</span>
                </div>
              </div>
            </div>

            {/* WALMART ROUTING DATA Section - ACTIVE */}
            <div className="relative">
              <div className="absolute inset-0 border-2 border-dashed border-purple-300 rounded-xl pointer-events-none"></div>
              <div className="absolute -top-3 left-6 bg-slate-50 px-2">
                <span className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                  <Truck size={12} />
                  WALMART ROUTING DATA
                </span>
              </div>
              <div className="absolute -top-3 right-6 bg-slate-50 px-2">
                <span className="text-[10px] text-slate-400">Required - Walmart/Sam's routing data</span>
              </div>
              <div className="p-4 pt-6 grid grid-cols-4 gap-4">
                {/* Walmart TMS Card */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${
                        uploadStatus.walmart_tms.status === 'active'
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-purple-100 border-2 border-dashed border-purple-400'
                      }`}>
                        {uploadStatus.walmart_tms.status === 'active'
                          ? <CheckCircle2 size={12} className="text-green-500" />
                          : <Truck size={12} className="text-purple-500" />
                        }
                      </div>
                      <span className="text-sm font-semibold text-slate-700">Walmart TMS</span>
                    </div>
                    {uploadStatus.walmart_tms.status === 'active' && (
                      <div className="text-right">
                        <div className="text-[10px] text-green-600 font-medium">{safeValue(uploadStatus.walmart_tms.rowCount)?.toLocaleString()} rows</div>
                        <div className="text-[10px] text-slate-400">{safeValue(uploadStatus.walmart_tms.lastUpload)}</div>
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mb-2">Load IDs, carriers, appointments</div>
                  <RoutingFileUpload
                    tenantId={tenantId}
                    fileType="routing_walmart_tms"
                    compact
                    onUploadComplete={(result) => handleUploadComplete('walmart_tms', result)}
                    onUploadError={(error) => handleUploadError('walmart_tms', error)}
                  />
                </div>

                {/* Store Data Card */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${
                        uploadStatus.store_data.status === 'active'
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-purple-100 border-2 border-dashed border-purple-400'
                      }`}>
                        {uploadStatus.store_data.status === 'active'
                          ? <CheckCircle2 size={12} className="text-green-500" />
                          : <Building size={12} className="text-purple-500" />
                        }
                      </div>
                      <span className="text-sm font-semibold text-slate-700">Store Data</span>
                    </div>
                    {uploadStatus.store_data.status === 'active' && (
                      <div className="text-[10px] text-green-600">{safeValue(uploadStatus.store_data.rowCount)} rows</div>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mb-2">DC/Store locations</div>
                  <RoutingFileUpload
                    tenantId={tenantId}
                    fileType="routing_store_data"
                    compact
                    onUploadComplete={(result) => handleUploadComplete('store_data', result)}
                    onUploadError={(error) => handleUploadError('store_data', error)}
                  />
                </div>

                {/* Transit Time Card */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${
                        uploadStatus.transit_time.status === 'active'
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-purple-100 border-2 border-dashed border-purple-400'
                      }`}>
                        {uploadStatus.transit_time.status === 'active'
                          ? <CheckCircle2 size={12} className="text-green-500" />
                          : <Clock size={12} className="text-purple-500" />
                        }
                      </div>
                      <span className="text-sm font-semibold text-slate-700">Transit Time</span>
                    </div>
                    {uploadStatus.transit_time.status === 'active' && (
                      <div className="text-[10px] text-green-600">{safeValue(uploadStatus.transit_time.rowCount)} rows</div>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mb-2">Origin to destination days</div>
                  <RoutingFileUpload
                    tenantId={tenantId}
                    fileType="routing_transit_time"
                    compact
                    onUploadComplete={(result) => handleUploadComplete('transit_time', result)}
                    onUploadError={(error) => handleUploadError('transit_time', error)}
                  />
                </div>

                {/* + More Placeholder */}
                <div className="border-2 border-dashed border-purple-300 rounded-lg flex items-center justify-center min-h-[100px]">
                  <span className="text-sm text-purple-400">+ More TMS</span>
                </div>
              </div>
            </div>

            {/* DATA TRANSFORMATION Section */}
            <div className="relative">
              <div className="absolute inset-0 border-2 border-dashed border-slate-300 rounded-xl pointer-events-none"></div>
              <div className="absolute -top-3 left-6 bg-slate-50 px-2">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                  <RefreshCw size={12} />
                  DATA TRANSFORMATION
                </span>
              </div>
              <div className="p-4 pt-6 grid grid-cols-4 gap-4">
                {/* Transform Card */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-bold">2</div>
                        <div className="flex items-center gap-1">
                          <RefreshCw size={14} className={extractionStatus.loading ? "text-blue-500 animate-spin" : "text-slate-500"} />
                          <span className="text-sm font-semibold text-slate-700">Transform</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                      <FileText size={10} />
                      SQL Transform
                    </div>
                    <button
                      onClick={handleRunExtraction}
                      disabled={extractionStatus.loading}
                      className={`mt-3 w-full py-1.5 text-white text-xs rounded flex items-center justify-center gap-1 transition-colors ${
                        extractionStatus.loading
                          ? 'bg-blue-400 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                    >
                      <RefreshCw size={10} className={extractionStatus.loading ? "animate-spin" : ""} />
                      {extractionStatus.loading ? 'Running...' : 'Run'}
                    </button>
                  </div>
                </div>

                {/* Done Card */}
                <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${
                  extractionStatus.results?.success ? 'border-green-300' :
                  extractionStatus.error ? 'border-red-300' : 'border-slate-200'
                }`}>
                  <div className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                          extractionStatus.results?.success ? 'bg-green-100 text-green-600' :
                          extractionStatus.error ? 'bg-red-100 text-red-600' : 'bg-slate-200 text-slate-600'
                        }`}>3</div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 size={14} className={
                            extractionStatus.results?.success ? "text-green-500" :
                            extractionStatus.error ? "text-red-500" : "text-slate-400"
                          } />
                          <span className="text-sm font-semibold text-slate-700">
                            {extractionStatus.results ? 'Complete' : extractionStatus.error ? 'Error' : 'Done'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      {extractionStatus.loading && (
                        <span className="text-xs text-blue-500">Processing...</span>
                      )}
                      {extractionStatus.results && !extractionStatus.loading && (
                        <div className="text-xs space-y-1">
                          <div className="text-green-600 font-medium">
                            {extractionStatus.results.summary?.success || 0} tables updated
                          </div>
                          {extractionStatus.results.summary?.failed > 0 && (
                            <div className="text-amber-600">
                              {extractionStatus.results.summary.failed} failed
                            </div>
                          )}
                        </div>
                      )}
                      {extractionStatus.error && !extractionStatus.loading && (
                        <span className="text-xs text-red-500">{extractionStatus.error}</span>
                      )}
                      {!extractionStatus.results && !extractionStatus.error && !extractionStatus.loading && (
                        <span className="text-xs text-slate-400">Waiting...</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* View SQL Card */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden flex items-center justify-center min-h-[100px]">
                  <button className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                    {'<>'} View SQL
                  </button>
                </div>

                {/* Results Summary */}
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Results</div>
                    {extractionStatus.results?.results ? (
                      <div className="space-y-1 text-xs">
                        {Object.entries(extractionStatus.results.results).map(([table, result]) => (
                          <div key={table} className="flex items-center justify-between">
                            <span className="text-slate-600 truncate" style={{maxWidth: '120px'}}>
                              {table.replace('routing_', '')}
                            </span>
                            {result.success ? (
                              <span className="text-green-600">{result.rowCount?.toLocaleString()}</span>
                            ) : (
                              <span className="text-red-400 text-[10px]" title={result.error}>{result.error?.substring(0, 20) || 'Error'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Run transform to see results</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        </div>
      )}

      {/* ==================== NOTES TAB ==================== */}
      {activeTab === 'notes' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header with access notice */}
          <div className="px-6 py-4 border-b border-slate-200 bg-green-50/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare size={20} className="text-green-600" />
                  Routing Notes
                </h2>
                <p className="text-sm text-slate-500 mt-1">Add notes to track issues, appointments, and changes</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-lg">
                <Lock size={14} className="text-amber-600" />
                <span className="text-xs font-medium text-amber-700">User-Approved Access Only</span>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Search Bar */}
            <div className="flex items-center gap-3">
              <select
                value={notesSearch.type}
                onChange={(e) => setNotesSearch(prev => ({ ...prev, type: e.target.value }))}
                className="text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="po">PO</option>
                <option value="pick_ticket">Pick Ticket</option>
                <option value="load">Load ID</option>
              </select>
              <input
                type="text"
                value={notesSearch.value}
                onChange={(e) => setNotesSearch(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleNotesSearch()}
                placeholder={`Enter ${notesSearch.type === 'po' ? 'PO' : notesSearch.type === 'pick_ticket' ? 'PT' : 'Load'}...`}
                className="text-sm border border-slate-200 rounded-md px-3 py-2 w-48 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button onClick={handleNotesSearch} className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors">
                Search
              </button>
              {searchedOrder && (
                <button onClick={resetNoteForm} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>
              )}
            </div>

            {/* Order Details */}
            {searchedOrder && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Order Details</h3>
                <div className="grid grid-cols-5 gap-x-4 gap-y-2 text-sm">
                  <div><span className="text-slate-500">PO:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.po_number) || '—'}</span></div>
                  <div><span className="text-slate-500">Load:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.load_id) || '—'}</span></div>
                  <div><span className="text-slate-500">Dest:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.po_dest) || '—'}</span></div>
                  <div><span className="text-slate-500">MABD:</span> <span className="font-medium text-slate-700">{formatShortDate(safeValue(searchedOrder.mabd)) || '—'}</span></div>
                  <div><span className="text-slate-500">Cases:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.cases) || '—'}</span></div>
                  <div><span className="text-slate-500">Stage:</span> <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    safeValue(searchedOrder.routing_status) === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                    safeValue(searchedOrder.routing_status) === 'Scheduled' ? 'bg-green-100 text-green-700' :
                    safeValue(searchedOrder.routing_status) === 'Confirmed' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>{safeValue(searchedOrder.routing_status) || '—'}</span></div>
                  <div><span className="text-slate-500">Ship Pt:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.ship_point) || '—'}</span></div>
                  <div><span className="text-slate-500">Ship Date:</span> <span className="font-medium text-slate-700">{formatShortDate(safeValue(searchedOrder.carrier_pu_date)) || '—'}</span></div>
                  <div><span className="text-slate-500">Carrier:</span> <span className="font-medium text-slate-700">{safeValue(searchedOrder.carrier_name) || '—'}</span></div>
                </div>
              </div>
            )}

            {/* Note Entry Form */}
            <div className="border border-slate-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Add Note</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">MABD</label>
                  <input type="text" value={noteForm.mabd} onChange={(e) => setNoteForm(p => ({ ...p, mabd: e.target.value }))} placeholder="MM/DD" className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Original Appt</label>
                  <input type="text" value={noteForm.original_appt_date} onChange={(e) => setNoteForm(p => ({ ...p, original_appt_date: e.target.value }))} placeholder="MM/DD/YYYY" className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Override Appt</label>
                  <input type="text" value={noteForm.override_appt_date} onChange={(e) => setNoteForm(p => ({ ...p, override_appt_date: e.target.value }))} placeholder="MM/DD/YYYY" className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
                  <select value={noteForm.reason} onChange={(e) => setNoteForm(p => ({ ...p, reason: e.target.value }))} className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500">
                    {REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Responsibility</label>
                  <select value={noteForm.responsibility} onChange={(e) => setNoteForm(p => ({ ...p, responsibility: e.target.value }))} className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500">
                    {RESPONSIBILITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Note</label>
                  <input type="text" value={noteForm.note} onChange={(e) => setNoteForm(p => ({ ...p, note: e.target.value }))} placeholder="Enter note details..." className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSaveNoteFromTab}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    disabled={!searchedOrder}
                  >
                    <Save size={14} />
                    {routingNotes[safeValue(searchedOrder?.po_number)] ? 'Update Note' : 'Save Note'}
                  </button>
                </div>
              </div>

              {/* Ticket Tracking */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <h4 className="text-xs font-semibold text-slate-600 mb-3">Ticket Tracking (for chargeback defense)</h4>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={noteForm.ticket_sent || false}
                      onChange={(e) => setNoteForm(p => ({ ...p, ticket_sent: e.target.checked }))}
                      className="rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    Ticket Sent
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-600">Ticket #:</label>
                    <input
                      type="text"
                      value={noteForm.ticket_number || ''}
                      onChange={(e) => setNoteForm(p => ({ ...p, ticket_number: e.target.value }))}
                      placeholder="Enter ticket number"
                      className="text-sm border border-slate-200 rounded-md px-3 py-1.5 w-40 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Notes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">Recent Notes</h3>
                <span className="text-xs text-slate-500">{routingNotesList.length} notes</span>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Date</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">PO</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Override</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Reason</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Responsibility</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Note</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-600">Ticket</th>
                      <th className="px-4 py-2 text-center font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notesLoading ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                          Loading notes...
                        </td>
                      </tr>
                    ) : routingNotesList.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                          No notes yet. Search for a PO above to add a note.
                        </td>
                      </tr>
                    ) : (
                      routingNotesList.slice(0, 20).map((note) => {
                        // Format created_at date
                        const createdAt = note.created_at?.toDate ? note.created_at.toDate() : (note.created_at ? new Date(note.created_at) : null);
                        const dateStr = createdAt ? `${createdAt.getMonth() + 1}/${createdAt.getDate()}/${String(createdAt.getFullYear()).slice(2)}` : '—';

                        // Get reason color
                        const reasonColorClass =
                          note.reason === 'carrier_no_show' ? 'bg-red-100 text-red-700' :
                          note.reason === 'rollover' ? 'bg-orange-100 text-orange-700' :
                          note.reason === 'customer_request' ? 'bg-blue-100 text-blue-700' :
                          note.reason === 'warehouse_delay' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-slate-100 text-slate-700';

                        return (
                          <tr key={note.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-600">{dateStr}</td>
                            <td className="px-4 py-2 font-medium text-blue-600">{note.po_number || '—'}</td>
                            <td className="px-4 py-2">
                              {note.override_pickup_date ? (
                                <span className="text-amber-600 font-medium">{note.override_pickup_date}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {note.reason ? (
                                <span className={`px-1.5 py-0.5 rounded text-xs ${reasonColorClass}`}>
                                  {getReasonLabel(note.reason)}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-slate-600">{getResponsibilityLabel(note.responsibility) || '—'}</td>
                            <td className="px-4 py-2 text-slate-600 max-w-[200px] truncate" title={note.note_text}>{note.note_text || '—'}</td>
                            <td className="px-4 py-2 text-slate-500">
                              {note.ticket_sent && note.ticket_number ? (
                                <span className="text-green-600">{note.ticket_number}</span>
                              ) : note.ticket_number ? (
                                note.ticket_number
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={() => {
                                  // Find the order for this note to open modal
                                  const order = routingData.orders.find(o => safeValue(o.po_number) === note.po_number);
                                  if (order) {
                                    openNoteModal(order, note);
                                  } else {
                                    // Open modal with minimal order data from note
                                    openNoteModal({
                                      po_number: note.po_number,
                                      load_id: note.load_id,
                                      mabd: note.mabd,
                                      warehouse: note.warehouse,
                                      po_dest: note.destination,
                                      cases: note.cases,
                                    }, note);
                                  }
                                }}
                                className="text-slate-400 hover:text-slate-600"
                                title="Edit note"
                              >
                                <Edit3 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DOCUMENTS TAB (BOL/POD OCR) ==================== */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FileText size={20} className="text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">BOL/POD Documents</h2>
                  <p className="text-sm text-slate-500">Upload and manage Bills of Lading and Proof of Delivery</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Stats badges */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    {documents.filter(d => d.status === 'matched').length} Matched
                  </span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                    {documents.filter(d => d.status === 'unmatched').length} Unmatched
                  </span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {documents.filter(d => d.status === 'processing').length} Processing
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Upload Area */}
            <div className="border-2 border-dashed border-orange-200 rounded-xl p-8 bg-orange-50/30 hover:bg-orange-50/50 transition-colors">
              <div className="text-center">
                <Upload size={40} className="mx-auto text-orange-400 mb-3" />
                <h3 className="text-lg font-medium text-slate-700 mb-1">Upload BOL/POD Document</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Drag and drop an image or PDF, or click to browse
                </p>
                <div className="flex items-center justify-center gap-3">
                  <label className="relative">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          setUploadingDocument(true);
                          await DocumentsService.uploadDocument(file, 'bol');
                          e.target.value = ''; // Reset input
                        } catch (error) {
                          console.error('Upload failed:', error);
                          alert('Failed to upload document: ' + error.message);
                        } finally {
                          setUploadingDocument(false);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadingDocument}
                    />
                    <span className={`inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors ${uploadingDocument ? 'opacity-50' : ''}`}>
                      {uploadingDocument ? (
                        <>
                          <RefreshCw size={16} className="animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={16} />
                          Upload BOL
                        </>
                      )}
                    </span>
                  </label>
                  <label className="relative">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          setUploadingDocument(true);
                          await DocumentsService.uploadDocument(file, 'pod');
                          e.target.value = '';
                        } catch (error) {
                          console.error('Upload failed:', error);
                          alert('Failed to upload document: ' + error.message);
                        } finally {
                          setUploadingDocument(false);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadingDocument}
                    />
                    <span className={`inline-flex items-center gap-2 px-4 py-2 bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors ${uploadingDocument ? 'opacity-50' : ''}`}>
                      <Upload size={16} />
                      Upload POD
                    </span>
                  </label>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Supported formats: JPG, PNG, PDF (max 10MB)
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                  placeholder="Search by PO, Load ID, or filename..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>
              <select
                value={documentFilter}
                onChange={(e) => setDocumentFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              >
                <option value="all">All Documents</option>
                <option value="matched">Matched</option>
                <option value="unmatched">Unmatched</option>
                <option value="processing">Processing</option>
                <option value="error">Errors</option>
              </select>
            </div>

            {/* Documents Table */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Document</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Extracted PO</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Load ID</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Cases/Pallets</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Uploaded</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documentsLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                        <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                        Loading documents...
                      </td>
                    </tr>
                  ) : documents.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                        <FileText size={32} className="mx-auto mb-2 text-slate-300" />
                        No documents uploaded yet. Upload a BOL or POD to get started.
                      </td>
                    </tr>
                  ) : (
                    documents
                      .filter(doc => {
                        // Status filter
                        if (documentFilter !== 'all' && doc.status !== documentFilter) return false;
                        // Search filter - searches filename, POs, Load ID, and full document text
                        if (documentSearch) {
                          const search = documentSearch.toLowerCase();
                          // Check all PO numbers if available
                          const poNumbersMatch = doc.extractedData?.po_numbers?.some(po => po?.toLowerCase().includes(search));
                          const matchesSearch = (
                            doc.fileName?.toLowerCase().includes(search) ||
                            doc.extractedData?.po_number?.toLowerCase().includes(search) ||
                            poNumbersMatch ||
                            doc.extractedData?.load_id?.toLowerCase().includes(search) ||
                            doc.matchedPo?.toLowerCase().includes(search) ||
                            doc.matchedLoadId?.toLowerCase().includes(search) ||
                            doc.extractedData?.rawText?.toLowerCase().includes(search)  // Full text search
                          );
                          if (!matchesSearch) return false;
                        }
                        return true;
                      })
                      .map((doc) => {
                        // Format upload date
                        const uploadDate = doc.createdAt?.toDate ? doc.createdAt.toDate() : (doc.createdAt ? new Date(doc.createdAt) : null);
                        const dateStr = uploadDate ? `${uploadDate.getMonth() + 1}/${uploadDate.getDate()}/${String(uploadDate.getFullYear()).slice(2)}` : '—';

                        // Status badge styling
                        const statusStyles = {
                          processing: 'bg-blue-100 text-blue-700',
                          matched: 'bg-green-100 text-green-700',
                          unmatched: 'bg-yellow-100 text-yellow-700',
                          error: 'bg-red-100 text-red-700'
                        };

                        return (
                          <tr key={doc.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Image size={16} className="text-slate-400" />
                                <span className="font-medium text-slate-700 truncate max-w-[150px]" title={doc.fileName}>
                                  {doc.fileName}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                doc.documentType === 'bol' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {doc.documentType?.toUpperCase() || 'BOL'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-blue-600">
                                {doc.matchedPo || doc.extractedData?.po_number || '—'}
                                {doc.extractedData?.po_numbers?.length > 1 && (
                                  <span className="ml-1 text-xs text-slate-500">
                                    (+{doc.extractedData.po_numbers.length - 1} more)
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-mono text-slate-600">
                                {doc.matchedLoadId || doc.extractedData?.load_id || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {doc.extractedData?.cases || doc.extractedData?.pallets ? (
                                <span>
                                  {doc.extractedData.cases && `${doc.extractedData.cases} cs`}
                                  {doc.extractedData.cases && doc.extractedData.pallets && ' / '}
                                  {doc.extractedData.pallets && `${doc.extractedData.pallets} plt`}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusStyles[doc.status] || 'bg-slate-100 text-slate-600'}`}>
                                {doc.status === 'processing' && <RefreshCw size={10} className="inline animate-spin mr-1" />}
                                {doc.status?.charAt(0).toUpperCase() + doc.status?.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500">{dateStr}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => setSelectedDocument(selectedDocument?.id === doc.id ? null : doc)}
                                  className={`p-1.5 rounded transition-colors ${selectedDocument?.id === doc.id ? 'text-orange-600 bg-orange-50' : 'text-slate-400 hover:text-orange-600 hover:bg-orange-50'}`}
                                  title="View Extracted Data"
                                >
                                  <FileText size={14} />
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const url = await DocumentsService.getViewUrl(doc.id);
                                      window.open(url, '_blank');
                                    } catch (error) {
                                      alert('Failed to open document: ' + error.message);
                                    }
                                  }}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="View PDF"
                                >
                                  <ExternalLink size={14} />
                                </button>
                                {doc.status === 'unmatched' && (
                                  <button
                                    onClick={() => setManualMatchModal({ open: true, document: doc })}
                                    className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                    title="Manual Match"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Delete this document?')) return;
                                    try {
                                      await DocumentsService.deleteDocument(doc.id);
                                    } catch (error) {
                                      alert('Failed to delete: ' + error.message);
                                    }
                                  }}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>

            {/* Extracted Data Modal (for selected document) */}
            {selectedDocument && (
              <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
                <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedDocument(null)} />
                <div className="flex min-h-full items-center justify-center p-4" style={{ zIndex: 10000, position: 'relative' }}>
                  <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                          <FileText size={20} className="text-orange-500" />
                          OCR Extracted Data
                        </h3>
                        <button
                          onClick={() => setSelectedDocument(null)}
                          className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      <div className="text-sm text-slate-500 mt-1">{selectedDocument.fileName}</div>
                    </div>

                    <div className="p-6 overflow-y-auto flex-1">
                      {/* Parsed Fields */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1">
                            PO Number{selectedDocument.extractedData?.po_numbers?.length > 1 ? 's' : ''}
                            {selectedDocument.extractedData?.po_numbers?.length > 1 && (
                              <span className="ml-1 text-blue-500">({selectedDocument.extractedData.po_numbers.length})</span>
                            )}
                          </div>
                          <div className="font-mono text-sm font-medium text-blue-600">
                            {selectedDocument.extractedData?.po_numbers?.length > 0
                              ? selectedDocument.extractedData.po_numbers[0]
                              : (selectedDocument.extractedData?.po_number || '—')}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1">Load ID</div>
                          <div className="font-mono text-sm font-medium text-slate-700">
                            {selectedDocument.extractedData?.load_id || '—'}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1">Cases</div>
                          <div className="font-mono text-sm font-medium text-slate-700">
                            {selectedDocument.extractedData?.cases || '—'}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1">Pallets</div>
                          <div className="font-mono text-sm font-medium text-slate-700">
                            {selectedDocument.extractedData?.pallets || '—'}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1">Weight</div>
                          <div className="font-mono text-sm font-medium text-slate-700">
                            {selectedDocument.extractedData?.weight ? `${selectedDocument.extractedData.weight.toLocaleString()} lbs` : '—'}
                          </div>
                        </div>
                      </div>

                      {/* All PO Numbers (if multiple) */}
                      {selectedDocument.extractedData?.po_numbers?.length > 1 && (
                        <div className="mb-4">
                          <div className="text-sm font-medium text-slate-700 mb-2">
                            All PO Numbers ({selectedDocument.extractedData.po_numbers.length})
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedDocument.extractedData.po_numbers.map((po, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-mono text-xs">
                                {po}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Status & Error */}
                      {selectedDocument.error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                          <span className="font-medium">Error:</span> {selectedDocument.error}
                        </div>
                      )}

                      {/* Raw Text */}
                      {selectedDocument.extractedData?.rawText ? (
                        <div>
                          <div className="text-sm font-medium text-slate-700 mb-2">
                            Raw OCR Text ({selectedDocument.extractedData.rawText.length.toLocaleString()} chars)
                          </div>
                          <pre className="text-xs text-slate-600 bg-slate-50 p-4 rounded-lg border border-slate-200 max-h-64 overflow-auto whitespace-pre-wrap font-mono">
                            {selectedDocument.extractedData.rawText}
                          </pre>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500 italic">No raw text available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Match Modal */}
      {manualMatchModal.open && manualMatchModal.document && (
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="fixed inset-0 bg-black/50" onClick={() => setManualMatchModal({ open: false, document: null })} />
          <div className="flex min-h-full items-center justify-center p-4" style={{ zIndex: 10000, position: 'relative' }}>
            <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl">
              <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white rounded-t-xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-800">Manual Match Document</h3>
                  <button onClick={() => setManualMatchModal({ open: false, document: null })} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                  <span className="font-medium">File:</span> {manualMatchModal.document.fileName}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
                  <input
                    type="text"
                    id="manualMatchPO"
                    defaultValue={manualMatchModal.document.extractedData?.po_number || ''}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter PO number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Load ID</label>
                  <input
                    type="text"
                    id="manualMatchLoadId"
                    defaultValue={manualMatchModal.document.extractedData?.load_id || ''}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter Load ID"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 rounded-b-xl flex justify-end gap-3">
                <button
                  onClick={() => setManualMatchModal({ open: false, document: null })}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const po = document.getElementById('manualMatchPO')?.value;
                    const loadId = document.getElementById('manualMatchLoadId')?.value;
                    if (!po && !loadId) {
                      alert('Please enter a PO number or Load ID');
                      return;
                    }
                    try {
                      await DocumentsService.manualMatch(manualMatchModal.document.id, loadId, po);
                      setManualMatchModal({ open: false, document: null });
                    } catch (error) {
                      alert('Failed to match: ' + error.message);
                    }
                  }}
                  className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Save Match
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== TRANSIT TIME TAB ==================== */}
      {activeTab === 'transit' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <Clock size={20} className="text-cyan-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Transit Time Data</h2>
                  <p className="text-sm text-slate-500">View shipping duration from origin to destination</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Last updated: —</span>
                <button className="px-3 py-1.5 bg-cyan-100 hover:bg-cyan-200 text-cyan-700 text-sm rounded-lg flex items-center gap-1 transition-colors">
                  <Upload size={14} />
                  Upload Data
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Search/Filter */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by origin, destination, or DC..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>
              <select
                value={selectedTransitMode || ''}
                onChange={(e) => setSelectedTransitMode(e.target.value || null)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
              >
                <option value="">All Modes</option>
                <option value="TL">TL (Truck Load)</option>
                <option value="Rail">Rail</option>
                <option value="Fleet">Fleet (LTL)</option>
              </select>
              <select
                value={selectedTransitOrigin || ''}
                onChange={(e) => setSelectedTransitOrigin(e.target.value || null)}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
              >
                <option value="">All Origins</option>
                {shipPoints.filter(sp => sp.active).map(sp => (
                  <option key={sp.code} value={sp.code}>{sp.code} - {sp.city || sp.location}</option>
                ))}
              </select>
              {(selectedTransitDest || selectedTransitOrigin || selectedTransitMode) && (
                <button
                  onClick={() => {
                    setSelectedTransitDest(null);
                    setSelectedTransitOrigin(null);
                    setSelectedTransitMode(null);
                  }}
                  className="px-3 py-2 text-sm bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-lg"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Interactive Transit Map */}
            <div className="mb-6 border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-cyan-600" />
                  <span className="font-semibold text-slate-700">Transit Routes Map</span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-slate-500">Origin</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: TYPE_COLORS.DC }}></div>
                    <span className="text-slate-500">DC</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: TYPE_COLORS.CP }}></div>
                    <span className="text-slate-500">CP</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: TYPE_COLORS.Store }}></div>
                    <span className="text-slate-500">Store</span>
                  </div>
                </div>
              </div>
              <TransitTimeMap
                shipPoints={shipPoints.filter(sp => sp.active)}
                transitRoutes={transitRoutes}
                destinations={transitDestinations}
                selectedDestination={selectedTransitDest}
                onSelectDestination={setSelectedTransitDest}
                selectedOrigin={selectedTransitOrigin}
                onSelectOrigin={setSelectedTransitOrigin}
                selectedMode={selectedTransitMode}
                height={350}
              />
            </div>

            {/* Transit Time Table */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                      <div className="flex items-center gap-1"><Warehouse size={12} /> Origin</div>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                      <div className="flex items-center gap-1"><MapPin size={12} /> Destination</div>
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 border-b border-slate-200">
                      <div className="flex items-center justify-center gap-1"><Truck size={12} /> Mode</div>
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 border-b border-slate-200">
                      <div className="flex items-center justify-center gap-1"><Clock size={12} /> Days</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sampleTransitRoutes
                    .filter(route => !selectedTransitOrigin || route.origin === selectedTransitOrigin)
                    .filter(route => !selectedTransitMode || route.mode === selectedTransitMode)
                    .filter(route => !selectedTransitDest || route.destId === selectedTransitDest)
                    .slice(0, 10) // Limit display
                    .map((route, idx) => {
                    const dest = getDestById(route.destId);
                    const originShipPoint = shipPoints.find(sp => sp.code === route.origin);
                    const isHighlighted = selectedTransitDest === route.destId;
                    const ModeIcon = route.mode === 'Rail' ? Train : route.mode === 'Fleet' ? Package : Truck;
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-slate-100 cursor-pointer transition-colors ${
                          isHighlighted ? 'bg-cyan-50' : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedTransitDest(isHighlighted ? null : route.destId)}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="font-medium text-slate-700">{route.origin}</span>
                            <span className="text-xs text-slate-400">{originShipPoint?.city || originShipPoint?.location}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-600">{dest?.name}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] rounded`} style={{
                              backgroundColor: `${TYPE_COLORS[dest?.type] || '#64748b'}20`,
                              color: TYPE_COLORS[dest?.type] || '#64748b'
                            }}>{dest?.type}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded`} style={{
                            backgroundColor: `${MODE_COLORS[route.mode] || '#64748b'}20`,
                            color: MODE_COLORS[route.mode] || '#64748b'
                          }}>
                            <ModeIcon size={10} />
                            {route.mode}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-semibold text-cyan-600">{route.days}d</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sampleTransitRoutes.length > 10 && (
                <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 text-center">
                  Showing 10 of {sampleTransitRoutes.length} routes. Use filters to narrow results.
                </div>
              )}
            </div>

            {/* Empty State - shown when no data uploaded */}
            <div className="hidden mt-8 text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
              <Clock size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Transit Time Data</h3>
              <p className="text-sm text-slate-400 mb-4">Upload transit time data to see shipping durations</p>
              <button className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm rounded-lg flex items-center gap-2 mx-auto transition-colors">
                <Upload size={16} />
                Upload Transit Data
              </button>
            </div>

            {/* Summary Stats */}
            <div className="mt-6 grid grid-cols-5 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">{sampleTransitRoutes.length}</div>
                <div className="text-xs text-slate-500">Total Routes</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-cyan-600">
                  {sampleTransitRoutes.length > 0 ? (sampleTransitRoutes.reduce((sum, r) => sum + r.days, 0) / sampleTransitRoutes.length).toFixed(1) : '0'}
                </div>
                <div className="text-xs text-slate-500">Avg Transit Days</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">{shipPoints.filter(sp => sp.active).length}</div>
                <div className="text-xs text-slate-500">Origins</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">{transitDestinations.length}</div>
                <div className="text-xs text-slate-500">Destinations</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">{new Set(sampleTransitRoutes.map(r => r.mode)).size}</div>
                <div className="text-xs text-slate-500">Modes</div>
              </div>
            </div>

            {/* MABD Calculator */}
            <div className="mt-6 border border-cyan-200 rounded-xl overflow-hidden bg-gradient-to-r from-cyan-50 to-white">
              <div className="px-4 py-3 bg-cyan-100/50 border-b border-cyan-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator size={18} className="text-cyan-600" />
                  <span className="font-semibold text-cyan-800">MABD Calculator</span>
                  <span className="px-2 py-0.5 bg-cyan-200 text-cyan-700 text-[10px] font-medium rounded">BETA</span>
                </div>
                <span className="text-xs text-cyan-600">Find the best MABD by warehouse</span>
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-600 mb-4">
                  Calculate the closest possible MABD (Must Arrive By Date) for each warehouse based on transit times, excluding federal holidays.
                </p>

                {/* Calculator Form */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                  {/* Destination */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Ship To (Destination)</label>
                    <select
                      value={mabdCalcDest}
                      onChange={(e) => setMabdCalcDest(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                    >
                      <option value="">Select destination...</option>
                      <optgroup label="Destinations">
                        {transitDestinations.map(dest => (
                          <option key={dest.id} value={dest.id}>{dest.name} - {dest.city}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Ship Date */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Ship Date</label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="date"
                        value={mabdCalcShipDate}
                        onChange={(e) => setMabdCalcShipDate(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Calculate Button */}
                  <div className="flex items-end">
                    <button
                      onClick={calculateMABD}
                      disabled={mabdCalcLoading || !mabdCalcDest || !mabdCalcShipDate}
                      className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      {mabdCalcLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Calculating...
                        </>
                      ) : (
                        <>
                          <Calculator size={16} />
                          Calculate MABD
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Results */}
                <div className="border border-cyan-200 rounded-lg bg-white overflow-hidden">
                  {!mabdCalcResults ? (
                    <div className="text-center text-slate-400 py-6">
                      <Calendar size={24} className="mx-auto mb-1 text-cyan-300" />
                      <p className="text-xs text-slate-400">Select destination and ship date, then click Calculate</p>
                    </div>
                  ) : mabdCalcResults.error ? (
                    <div className="text-center text-red-500 py-4">
                      <AlertCircle size={20} className="mx-auto mb-1" />
                      <p className="text-xs">{mabdCalcResults.error}</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Origin</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">MABD</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">Days</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">Mode</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">
                            <button onClick={() => setMabdCalcResults(null)} className="text-slate-400 hover:text-slate-600">Clear</button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {mabdCalcResults.results.map((result, idx) => (
                          <tr key={idx} className={`border-b border-slate-100 ${result.isBest ? 'bg-green-50' : ''}`}>
                            <td className="px-3 py-2">
                              <span className="font-semibold text-slate-700">{result.origin}</span>
                              <span className="text-xs text-slate-400 ml-1">{result.city}</span>
                            </td>
                            <td className="px-3 py-2 font-semibold text-slate-800">{result.mabdFormatted}</td>
                            <td className="px-3 py-2 text-center text-slate-600">{result.transitDays}</td>
                            <td className="px-3 py-2 text-center">
                              <span className="px-2 py-0.5 text-xs rounded" style={{
                                backgroundColor: `${MODE_COLORS[result.mode] || '#64748b'}20`,
                                color: MODE_COLORS[result.mode] || '#64748b'
                              }}>{result.mode}</span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {result.isBest && <span className="text-xs text-green-600 font-medium">Best</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Info Note */}
                <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                  <AlertCircle size={14} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>
                    Federal holidays excluded: New Year's Day, MLK Day, Presidents' Day, Memorial Day, Independence Day, Labor Day, Columbus Day, Veterans Day, Thanksgiving, Christmas.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SETTINGS TAB ==================== */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                <Settings size={20} className="text-slate-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Settings</h2>
                <p className="text-sm text-slate-500">Configure ship points and manage users</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* ==================== SHIP POINTS SECTION ==================== */}
            {canViewSettings && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Warehouse size={18} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-700">Ship Points</h3>
                  <span className="text-xs text-slate-500">({shipPoints.length})</span>
                </div>
                {canManageShipPoints && (
                  <button
                    onClick={() => openShipPointModal()}
                    className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg flex items-center gap-1 transition-colors"
                  >
                    <span className="text-lg leading-none">+</span> Add Ship Point
                  </button>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-500 mb-4">Configure your ship points (origin warehouses). Same location can have multiple ship points for different departments.</p>

                {shipPointsLoading ? (
                  <div className="text-center py-8 text-slate-400">Loading ship points...</div>
                ) : shipPoints.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No ship points configured. Click "Add Ship Point" to create one.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {shipPoints.map((sp) => {
                      const colors = ['blue', 'purple', 'amber', 'cyan', 'green', 'rose', 'indigo'];
                      const colorIndex = sp.code ? sp.code.charCodeAt(0) % colors.length : 0;
                      const color = colors[colorIndex];
                      return (
                        <div key={sp.id} className={`p-4 rounded-lg border ${sp.active ? 'bg-slate-50 border-slate-200' : 'bg-slate-100 border-slate-300 opacity-60'}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-14 h-14 rounded-lg bg-${color}-100 border-2 border-${color}-200 flex items-center justify-center overflow-hidden`}>
                                <span className={`font-bold text-${color}-600 text-lg`}>{sp.code?.substring(0, 3) || '?'}</span>
                              </div>
                              <div>
                                <div className="font-semibold text-slate-700">{sp.name || sp.code}</div>
                                <div className="text-xs text-slate-500 font-mono">Code: {sp.code}</div>
                                {sp.location && <div className="text-xs text-slate-400">Location: {sp.location}</div>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={`px-2 py-0.5 text-xs rounded ${sp.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                                {sp.active ? 'Active' : 'Inactive'}
                              </span>
                              {canManageShipPoints && (
                                <>
                                  <button
                                    onClick={() => openShipPointModal(sp)}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleShipPointActive(sp)}
                                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                    title={sp.active ? 'Deactivate' : 'Activate'}
                                  >
                                    {sp.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {(sp.address || sp.city) && (
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">
                                {[sp.address, sp.city, sp.state, sp.zip].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* ==================== USERS SECTION ==================== */}
            {canViewSettings && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-700">Users</h3>
                  <span className="text-xs text-slate-500">({tenantUsers.length} / {tenantConfig.userCap} max)</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* CHA-97 / PR-C: real-time updates not supported
                      post-lockdown; click Refresh to re-fetch. */}
                  <button
                    onClick={refreshUsers}
                    disabled={usersLoading}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                    title="Refresh users (real-time updates not supported)"
                  >
                    <RefreshCw size={14} className={usersLoading ? 'animate-spin' : ''} /> Refresh
                  </button>
                  {canManageUsers && (
                    <button
                      onClick={() => openUserModal()}
                      disabled={tenantUsers.length >= tenantConfig.userCap}
                      className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <UserPlus size={14} /> Add User
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-500 mb-4">{canManageUsers ? 'Manage users who can access this tenant\'s routing data.' : 'Users who can access this tenant\'s routing data.'}</p>

                {usersLoading ? (
                  <div className="text-center py-8 text-slate-400">Loading users...</div>
                ) : tenantUsers.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No users configured. Click "Add User" to invite someone.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-slate-600">User</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-600">Role</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-600">Status</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-600">Last Login</th>
                          {canManageUsers && <th className="px-4 py-2 text-center font-medium text-slate-600">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {tenantUsers.map((user) => (
                          /* CHA-97 / PR-C: shape from listUsers callable —
                             uid + displayName + disabled (boolean) + roleId.
                             Orphan rows (Auth user missing — see CHA-118)
                             render at reduced opacity. */
                          <tr key={user.uid} className={`border-t border-slate-100 hover:bg-slate-50 ${user._orphan ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-700">
                                {user.displayName || user.email || '—'}
                                {user._orphan && <span className="ml-2 text-xs italic text-amber-700">(orphan)</span>}
                              </div>
                              <div className="text-xs text-slate-500">{user.email || '—'}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                user.roleId === 'admin' ? 'bg-purple-100 text-purple-700' :
                                user.roleId === 'manager' ? 'bg-blue-100 text-blue-700' :
                                user.roleId === 'user' ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {user.roleId || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {/* Status reflects Firebase Auth's `disabled` boolean.
                                  Pending users live in user_invitations (separate UI). */}
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                user.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {user.disabled ? 'inactive' : 'active'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">
                              {/* lastLogin no longer in the user shape post-CHA-97
                                  (admin.auth().getUsers returns lastSignInTime but
                                  listUsers doesn't currently expose it). Always —. */}
                              —
                            </td>
                            {canManageUsers && (
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => openUserModal(user)}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit"
                                    disabled={user._orphan}
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleSendPasswordReset(user.email)}
                                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                    title="Send password reset"
                                    disabled={user._orphan}
                                  >
                                    <Key size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleUserStatus(user)}
                                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                    title={user.disabled ? 'Enable' : 'Disable'}
                                    disabled={user._orphan}
                                  >
                                    {user.disabled ? <ToggleLeft size={14} /> : <ToggleRight size={14} />}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(user.uid)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Remove user"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Future Settings */}
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <span className="text-slate-400 text-2xl">+</span>
              </div>
              <span className="text-sm font-medium text-slate-500">Notification Preferences</span>
              <span className="text-xs text-slate-400 mt-1">Coming soon</span>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SHIP POINT MODAL ==================== */}
      {shipPointModalOpen && (
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9999 }} onClick={() => setShipPointModalOpen(false)} />
          <div className="flex min-h-full items-center justify-center p-4" style={{ zIndex: 10000, position: 'relative' }}>
            <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800">
                  {editingShipPoint ? 'Edit Ship Point' : 'Add Ship Point'}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
                    <input
                      type="text"
                      value={shipPointForm.code}
                      onChange={(e) => setShipPointForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                      placeholder="IMG"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!!editingShipPoint}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Location (for grouping)</label>
                    <input
                      type="text"
                      value={shipPointForm.location}
                      onChange={(e) => setShipPointForm(p => ({ ...p, location: e.target.value }))}
                      placeholder="Los Angeles"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={shipPointForm.name}
                    onChange={(e) => setShipPointForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="IMG Distribution Center"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={shipPointForm.address}
                    onChange={(e) => setShipPointForm(p => ({ ...p, address: e.target.value }))}
                    placeholder="1234 Industrial Blvd"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                    <input
                      type="text"
                      value={shipPointForm.city}
                      onChange={(e) => setShipPointForm(p => ({ ...p, city: e.target.value }))}
                      placeholder="Los Angeles"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                    <input
                      type="text"
                      value={shipPointForm.state}
                      onChange={(e) => setShipPointForm(p => ({ ...p, state: e.target.value.toUpperCase() }))}
                      placeholder="CA"
                      maxLength={2}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={shipPointForm.zip}
                      onChange={(e) => setShipPointForm(p => ({ ...p, zip: e.target.value }))}
                      placeholder="90001"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shipPointForm.active}
                    onChange={(e) => setShipPointForm(p => ({ ...p, active: e.target.checked }))}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  Active
                </label>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex justify-between">
                {editingShipPoint && (
                  <button
                    onClick={() => { handleDeleteShipPoint(editingShipPoint.id); setShipPointModalOpen(false); }}
                    className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Delete
                  </button>
                )}
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={() => setShipPointModalOpen(false)}
                    className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveShipPoint}
                    disabled={!shipPointForm.code}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editingShipPoint ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== USER MODAL ==================== */}
      {userModalOpen && (
        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9999 }} onClick={() => setUserModalOpen(false)} />
          <div className="flex min-h-full items-center justify-center p-4" style={{ zIndex: 10000, position: 'relative' }}>
            <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-800">
                  {editingUser ? 'Edit User' : 'Add User'}
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="user@company.com"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!!editingUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name
                    {editingUser && (
                      <span className="ml-2 text-xs text-slate-400 font-normal">
                        (editing temporarily unavailable — CHA-117)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={userForm.name}
                    // TODO(CHA-117): name editing is disabled in edit mode because
                    // CHA-97 (PR-C) routes user-mutation writes through the
                    // assignUserRole callable, which only persists role/tenant.
                    // The `name` field is dropped server-side until the
                    // userPreferences/CHA-117 surface lands. Create mode keeps
                    // name (it's stored on the user_invitations record).
                    onChange={(e) => setUserForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="John Smith"
                    disabled={!!editingUser}
                    title={editingUser ? 'Name editing temporarily unavailable (CHA-117)' : ''}
                    className={`w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      editingUser ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm(p => ({ ...p, role: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="viewer">Viewer (read-only)</option>
                    <option value="user">User (view + notes)</option>
                    <option value="manager">Manager (view + notes + uploads)</option>
                    <option value="admin">Admin (full access)</option>
                  </select>
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Temporary Password *</label>
                    <input
                      type="text"
                      value={userForm.tempPassword}
                      onChange={(e) => setUserForm(p => ({ ...p, tempPassword: e.target.value }))}
                      placeholder="Enter temporary password"
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">User will be prompted to reset this on first login.</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                <button
                  onClick={() => setUserModalOpen(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={!userForm.email || (!editingUser && !userForm.tempPassword)}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingUser ? 'Update' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PLAN TAB ==================== */}
      {activeTab === 'plan' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-8">
          {/* Overview */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <BookOpen size={16} className="text-purple-600" />
              </div>
              Overview
            </h2>
            <p className="text-slate-600">{PLAN_CONTENT.overview}</p>
          </section>

          {/* Data Approach */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Data Approach</h2>
            <p className="text-sm text-slate-600 mb-3">{PLAN_CONTENT.dataApproach}</p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800"><strong>Note:</strong> {PLAN_CONTENT.keyInsight}</p>
            </div>
          </section>

          {/* Status Flow */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Routing Status Flow</h2>
            <div className="flex flex-wrap items-center gap-2">
              {PLAN_CONTENT.statusFlow.map((step, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className={`px-3 py-2 rounded-lg text-sm ${
                    step.num === 1 ? 'bg-red-100 text-red-700' :
                    step.num === 2 ? 'bg-yellow-100 text-yellow-700' :
                    step.num === 3 ? 'bg-blue-100 text-blue-700' :
                    step.num === 4 ? 'bg-green-100 text-green-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    <div className="font-medium">{step.num}. {step.status}</div>
                    <div className="text-xs opacity-75">{step.desc}</div>
                  </div>
                  {idx < PLAN_CONTENT.statusFlow.length - 1 && (
                    <ArrowRight size={16} className="text-slate-300" />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Shipment Types */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Shipment Types</h2>
            <p className="text-sm text-slate-600 mb-3">Prepaid is not a routing stage - it's a shipment type that runs parallel to the status flow.</p>
            <div className="flex gap-4">
              {PLAN_CONTENT.shipmentTypes.map((t, idx) => (
                <div key={idx} className={`px-4 py-3 rounded-lg ${t.type === 'Collect' ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'}`}>
                  <div className={`font-medium ${t.type === 'Collect' ? 'text-blue-700' : 'text-purple-700'}`}>{t.type}</div>
                  <div className="text-xs text-slate-600 mt-1">{t.desc}</div>
                </div>
              ))}
            </div>
          </section>

          {/* UI Decisions */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">UI Design Decisions</h2>
            <ul className="space-y-2">
              {PLAN_CONTENT.uiDecisions.map((decision, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  {decision}
                </li>
              ))}
            </ul>
          </section>

          {/* Tickets */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Ticket Tracking</h2>
            <p className="text-sm text-slate-600">{PLAN_CONTENT.tickets}</p>
          </section>

          {/* Planned Components */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Planned UI Components</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PLAN_CONTENT.plannedComponents.map((comp, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded-lg">
                  <h3 className="font-medium text-slate-700">{comp.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{comp.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Dashboard Layout Grid */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Dashboard Layout Grid</h2>
            <p className="text-sm text-slate-600 mb-3">Named positions for each UI element in the dashboard:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-slate-600 w-16">Pos</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {PLAN_CONTENT.dashboardLayout.map((item, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-4 py-2 font-mono font-medium text-blue-600">{item.pos}</td>
                      <td className="px-4 py-2 font-medium text-slate-700">{item.name}</td>
                      <td className="px-4 py-2 text-slate-600">{item.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Tabs */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Application Tabs</h2>
            <div className="flex flex-wrap gap-3">
              {PLAN_CONTENT.tabs.map((tab, idx) => (
                <div key={idx} className={`px-4 py-3 rounded-lg border ${
                  tab.name === 'Dashboard' ? 'bg-blue-50 border-blue-200' :
                  tab.name === 'Data Workflow' ? 'bg-amber-50 border-amber-200' :
                  tab.name === 'Notes' ? 'bg-green-50 border-green-200' :
                  'bg-purple-50 border-purple-200'
                }`}>
                  <div className={`font-medium ${
                    tab.name === 'Dashboard' ? 'text-blue-700' :
                    tab.name === 'Data Workflow' ? 'text-amber-700' :
                    tab.name === 'Notes' ? 'text-green-700' :
                    'text-purple-700'
                  }`}>{tab.name}</div>
                  <div className="text-xs text-slate-600 mt-1">{tab.desc}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Walmart TMS Columns */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Walmart TMS Column Definitions</h2>
            <p className="text-sm text-slate-600 mb-3">Columns from wm_tms_img and wm_tms_img_dsdc tables:</p>
            <div className="overflow-x-auto max-h-[300px]">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-slate-600 border-b border-slate-200">Column</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600 border-b border-slate-200">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {PLAN_CONTENT.walmartTmsColumns.map((item, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-4 py-2 font-mono text-xs text-blue-600">{item.col}</td>
                      <td className="px-4 py-2 text-slate-600">{item.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Walmart Routing Terms */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Walmart Routing Terms</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PLAN_CONTENT.walmartTerms.map((item, idx) => (
                <div key={idx} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="font-mono font-bold text-yellow-700">{item.term}</div>
                  <div className="text-xs text-yellow-600 mt-1">{item.name}</div>
                  <div className="text-xs text-slate-600 mt-2">{item.desc}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Current Data Sources */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Current Data Sources (Step 3-5)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">Step</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">Source</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">BigQuery Table</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {PLAN_CONTENT.dataSources.map((ds, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-4 py-2 font-medium text-slate-700">{ds.step}</td>
                      <td className="px-4 py-2 text-slate-600">{ds.name}</td>
                      <td className="px-4 py-2 text-slate-600">{ds.source}</td>
                      <td className="px-4 py-2 text-xs text-slate-500 font-mono">{ds.table}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Future: Direct file upload in Routing interface (replace Google Sheets sync)</p>
          </section>

          {/* Complexity Points */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Complexity Points to Handle</h2>
            <ul className="space-y-2">
              {PLAN_CONTENT.complexityPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Circle size={8} className="text-slate-400 mt-1.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </section>

          {/* SaaS Business Features */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">SaaS Business Features (Future)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {PLAN_CONTENT.saasFeatures.map((feature, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-slate-700">{feature.name}</h3>
                    <span className={`px-2 py-0.5 text-[10px] rounded ${
                      feature.priority === 'High' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'
                    }`}>{feature.priority}</span>
                  </div>
                  <p className="text-xs text-slate-500">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps - Phased */}
          <section>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Roadmap</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Phase 1 */}
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-blue-50 border-b border-blue-200">
                  <span className="font-semibold text-blue-700 text-sm">Phase 1: Core Product</span>
                </div>
                <div className="p-3 space-y-2">
                  {PLAN_CONTENT.nextSteps.phase1.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      {item.done ? (
                        <CheckCircle2 size={14} className="text-green-500" />
                      ) : item.current ? (
                        <RefreshCw size={14} className="text-blue-500" />
                      ) : (
                        <Circle size={14} className="text-slate-300" />
                      )}
                      <span className={item.done ? 'text-slate-400 line-through' : item.current ? 'text-blue-600 font-medium' : 'text-slate-600'}>
                        {item.task}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase 2 */}
              <div className="border border-purple-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-purple-50 border-b border-purple-200">
                  <span className="font-semibold text-purple-700 text-sm">Phase 2: Multi-Tenant</span>
                </div>
                <div className="p-3 space-y-2">
                  {PLAN_CONTENT.nextSteps.phase2.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Circle size={14} className="text-slate-300" />
                      <span className="text-slate-600">{item.task}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase 3 */}
              <div className="border border-green-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-green-50 border-b border-green-200">
                  <span className="font-semibold text-green-700 text-sm">Phase 3: SaaS Launch</span>
                </div>
                <div className="p-3 space-y-2">
                  {PLAN_CONTENT.nextSteps.phase3.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Circle size={14} className="text-slate-300" />
                      <span className="text-slate-600">{item.task}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Note Modal */}
      <NoteModal
        isOpen={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          setNoteModalOrder(null);
          setNoteModalExisting(null);
        }}
        onSave={handleSaveNote}
        onDelete={handleDeleteNote}
        order={noteModalOrder}
        existingNote={noteModalExisting}
        tenantId={tenantId}
      />
    </div>
  );
}
